import {
    ActionIcon,
    Badge,
    Button,
    Card,
    Checkbox,
    Group,
    Loader,
    Modal,
    NumberInput,
    SegmentedControl,
    Select,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
    IconAlertTriangle,
    IconArrowRight,
    IconCheck,
    IconChevronLeft,
    IconDownload,
    IconPencil,
    IconPlus,
    IconRefresh,
    IconTrash,
    IconUpload,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState, type FC, type ReactNode } from 'react';

import ScheduleButton from '@components/ui/ScheduleAlertDialog/ScheduleButton';

import type {
    ApprovalPreview,
    ApprovalPreviewLine,
    ApprovalPreviewRoute,
    ProductHealthSnapshot,
    RebalanceNode,
    RebalancePlan,
    RebalancePlanTransfer,
    RebalanceScope,
    RebalanceWarehousePath,
    RecommendedTransfer,
    TransferLeg,
} from '@api/rebalance';
import { formatCount } from '@utils/currency';
import { showError, showSuccess } from '@utils/notifications';
import {
    approveRebalancePlan,
    createRebalancePlan,
    deleteRebalancePlan,
    getRebalanceApprovalPreview,
    getRebalanceNodes,
    getRebalancePaths,
    getRebalancePlan,
    getRebalancePlans,
    getRebalanceTransfers,
    pushRebalancePlanToWms,
    updateRebalancePlan,
} from '@api/rebalance';
import { getOrgPolicies } from '@api/organization';
import DataSheet, { type DataSheetColumn, type DataSheetFilterConfig } from '@components/ui/DataSheet';
import type { FilterValue } from '@components/ui/EntityFilterDropdown';
import { useProductFilterParams } from '@hooks/useProductFilterParams';
import styles from './RebalancePlansTab.module.css';

type View = 'list' | 'plan';

const statusColor = (s: string) => {
    switch (s) {
        case 'COMPLETED': return 'green';
        case 'EXECUTING': return 'blue';
        case 'APPROVED': return 'teal';
        case 'DRAFT': return 'orange';
        default: return 'gray';
    }
};

function downloadCsv(plan: RebalancePlan, transfers: RecommendedTransfer[], overrides?: Map<string, number>) {
    const headers = ['Transfer ID', 'SKU', 'Product Name', 'Category', 'Tier', 'Product Group', 'From Warehouse', 'To Warehouse', 'Recommended Qty', 'Approved Qty', 'Legs'];
    const rows = transfers.map(t => [
        t.id,
        t.skuCode ?? t.productId,
        t.productName ?? '',
        t.category ?? '',
        t.tier ?? '',
        t.productGroup ?? '',
        t.fromNodeName ?? t.fromWarehouseId,
        t.toNodeName ?? t.toWarehouseId ?? '',
        t.qtyRecommended,
        overrides?.get(t.id) ?? t.qtyApproved ?? t.qtyRecommended,
        (t.legs ?? []).map(l => `${l.fromWarehouseId}>${l.toWarehouseId}:${l.qty}`).join(' | '),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${plan.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function isPlanStale(plan: RebalancePlan): boolean {
    const planDay = new Date(plan.planDate).toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];
    return plan.status === 'DRAFT' && planDay < today;
}

// Aggregate-row cell for a Transfer Qty column: total units with the total CBM
// rendered alongside (mirrors the Distribution plan's plan-qty totals).
function renderQtyWithCbm(qty: number, cbm: number): ReactNode {
    if (cbm <= 0) return formatCount(qty);
    return (
        <span style={{ whiteSpace: 'nowrap' }}>
            {formatCount(qty)}
            <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.65, marginLeft: 6 }}>{cbm.toFixed(2)} CBM</span>
        </span>
    );
}

/** A grid row: a transfer, or one of its physical legs shown underneath it in a network plan. */
type PlanRow = RecommendedTransfer & { _isLeg?: boolean; _leg?: TransferLeg };

const planScopeOf = (plan: RebalancePlan | null): RebalanceScope =>
    plan?.parameters?.scope ?? { level: 'WAREHOUSE' };

const scopeLabel = (scope: RebalanceScope): string =>
    scope.level === 'NETWORK' ? 'Network' : scope.level === 'NODE' ? `Within ${scope.nodeName ?? 'node'}` : 'Warehouses';

const unallocatedReasonText = (reason: RecommendedTransfer['unallocatedReason']): string => {
    switch (reason) {
        case 'NO_LANE': return 'No active route connects a source warehouse with stock to a destination warehouse in this node pair. Add a route in Settings.';
        case 'SOURCES_AT_FLOOR': return 'Every source warehouse is at its keep floor. The node has surplus on paper, but no single warehouse can send more.';
        case 'DESTINATIONS_FULL': return 'Every destination warehouse is at capacity.';
        default: return 'Could not be placed on any leg.';
    }
};

// ─── Leg editor (network plans) ────────────────────────────────────────────────

const LegEditorModal: FC<{
    transfer: RecommendedTransfer | null;
    plan: RebalancePlan;
    nodes: RebalanceNode[];
    lanes: RebalanceWarehousePath[];
    nameOf: (id: string) => string;
    onClose: () => void;
    onSaved: (plan: RebalancePlan) => void;
}> = ({ transfer, plan, nodes, lanes, nameOf, onClose, onSaved }) => {
    const [legs, setLegs] = useState<TransferLeg[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLegs((transfer?.legs ?? []).map(l => ({ ...l })));
        setError(null);
    }, [transfer?.id]);

    if (!transfer) return null;

    const fromMembers = transfer.fromNodeId
        ? nodes.find(n => n.id === transfer.fromNodeId)?.members.map(m => m.warehouseId) ?? []
        : [transfer.fromWarehouseId];
    const toMembers = transfer.toNodeId
        ? nodes.find(n => n.id === transfer.toNodeId)?.members.map(m => m.warehouseId) ?? []
        : [transfer.toWarehouseId];
    const laneSet = new Set(lanes.filter(l => l.isActive && l.fromWarehouseId).map(l => `${l.fromWarehouseId}|${l.toWarehouseId}`));
    const hasLane = (from: string, to: string) => laneSet.has(`${from}|${to}`);

    const total = legs.reduce((a, l) => a + (Number(l.qty) || 0), 0);
    const target = transfer.qtyApproved ?? transfer.qtyRecommended;

    const updateLeg = (i: number, patch: Partial<TransferLeg>) =>
        setLegs(prev => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

    const save = async () => {
        const bad = legs.find(l => !hasLane(l.fromWarehouseId, l.toWarehouseId));
        if (bad) { setError(`No active route ${nameOf(bad.fromWarehouseId)} → ${nameOf(bad.toWarehouseId)}.`); return; }
        setSaving(true);
        setError(null);
        try {
            const updated = await updateRebalancePlan(plan.id, { transferLegEdits: { [transfer.id]: legs.filter(l => l.qty > 0) } });
            onSaved(updated);
            onClose();
        } catch (err: any) {
            const raw = err?.error?.message ?? err?.message;
            setError(Array.isArray(raw) ? raw.join('; ') : raw || 'Could not save legs.');
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        setSaving(true);
        setError(null);
        try {
            const updated = await updateRebalancePlan(plan.id, { transferLegResets: [transfer.id] });
            onSaved(updated);
            onClose();
        } catch {
            setError('Could not reset legs.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            opened
            onClose={onClose}
            title={<Text fw={600} size="sm">Legs · {transfer.productName ?? transfer.skuCode ?? transfer.productId}</Text>}
            size="lg"
            radius="md"
            centered
        >
            <Stack gap="sm">
                <Text size="xs" c="dimmed">
                    {nameOf(transfer.fromWarehouseId)} → {nameOf(transfer.toWarehouseId)} · approved {formatCount(target)} units.
                    Each leg is one physical shipment on an existing route; the approved quantity becomes the sum of the legs.
                </Text>
                {legs.map((leg, i) => (
                    <Group key={leg.legId} gap="xs" align="flex-end" wrap="nowrap">
                        <Select
                            label={i === 0 ? 'From' : undefined}
                            size="xs"
                            w={200}
                            data={fromMembers.map(id => ({ value: id, label: nameOf(id) }))}
                            value={leg.fromWarehouseId}
                            onChange={v => v && updateLeg(i, { fromWarehouseId: v })}
                        />
                        <Select
                            label={i === 0 ? 'To' : undefined}
                            size="xs"
                            w={200}
                            data={toMembers.map(id => ({ value: id, label: nameOf(id), disabled: !hasLane(leg.fromWarehouseId, id) }))}
                            value={leg.toWarehouseId}
                            onChange={v => v && updateLeg(i, { toWarehouseId: v })}
                        />
                        <NumberInput
                            label={i === 0 ? 'Qty' : undefined}
                            size="xs"
                            w={110}
                            min={0}
                            value={leg.qty}
                            onChange={v => updateLeg(i, { qty: v === '' ? 0 : Number(v) })}
                        />
                        {!hasLane(leg.fromWarehouseId, leg.toWarehouseId) && (
                            <Badge color="red" variant="light" size="xs">no route</Badge>
                        )}
                        <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => setLegs(prev => prev.filter((_, idx) => idx !== i))} title="Remove leg">
                            <IconTrash size={14} />
                        </ActionIcon>
                    </Group>
                ))}
                <Button
                    size="compact-xs"
                    variant="subtle"
                    w="fit-content"
                    leftSection={<IconPlus size={12} />}
                    onClick={() => setLegs(prev => [...prev, {
                        legId: `new-${Date.now()}`,
                        pathId: '',
                        fromWarehouseId: fromMembers[0] ?? transfer.fromWarehouseId,
                        toWarehouseId: toMembers.find(t => hasLane(fromMembers[0] ?? '', t)) ?? toMembers[0] ?? transfer.toWarehouseId,
                        qty: Math.max(0, target - total),
                        leadTimeDays: 0,
                        costPerUnit: null,
                    }])}
                >
                    Add leg
                </Button>
                <Group justify="space-between" mt="xs">
                    <Text size="xs" c={total === target ? 'dimmed' : 'orange.8'}>
                        Legs total {formatCount(total)}{total !== target ? ` (was ${formatCount(target)} — approved qty will follow the legs)` : ''}
                    </Text>
                    <Group gap="xs">
                        {transfer.legsEdited && transfer.suggestedLegs && (
                            <Button size="xs" variant="subtle" color="gray" loading={saving} onClick={reset}>Reset to suggested</Button>
                        )}
                        <Button size="xs" variant="subtle" color="gray" onClick={onClose}>Cancel</Button>
                        <Button size="xs" color="violet" loading={saving} onClick={save}>Save legs</Button>
                    </Group>
                </Group>
                {error && <Text size="xs" c="red">{error}</Text>}
            </Stack>
        </Modal>
    );
};

// ─── Approval review (what gets raised) ────────────────────────────────────────

const ApproveReviewModal: FC<{
    preview: ApprovalPreview | null;
    loading: boolean;
    approving: boolean;
    pushToWms: boolean;
    onPushToWmsChange: (v: boolean) => void;
    onCancel: () => void;
    onApprove: () => void;
}> = ({ preview, loading, approving, pushToWms, onPushToWmsChange, onCancel, onApprove }) => {
    // A route row expands to its STN lines (same row type: a line carries `_line`).
    type ReviewRow = ApprovalPreviewRoute & { _line?: ApprovalPreviewLine; _rowKey: string };
    const rows: ReviewRow[] = (preview?.routes ?? []).map(r => ({ ...r, _rowKey: `${r.fromWarehouseId}|${r.toWarehouseId}` }));
    const linesOf = (r: ReviewRow): ReviewRow[] | undefined =>
        r._line ? undefined : r.lines.map(l => ({ ...r, _line: l, _rowKey: `${r._rowKey}|${l.productId}` }));
    const columns: DataSheetColumn<ReviewRow>[] = [
        {
            key: 'fromWarehouseName',
            header: 'From',
            width: 220,
            render: (_, r) => r._line ? (
                <div>
                    <Text size="sm">{r._line.productName ?? r._line.skuCode ?? r._line.productId}</Text>
                    <Text size="xs" c="dimmed">{r._line.skuCode ?? r._line.productId}</Text>
                </div>
            ) : (
                <div>
                    <Text size="sm" fw={500}>{r.fromWarehouseName}</Text>
                    <Text size="xs" c="dimmed">{r.fromNodeName ?? 'not in a node'}{r.fromFacilityCode ? ` · ${r.fromFacilityCode}` : ''}</Text>
                </div>
            ),
        },
        {
            key: 'toWarehouseName',
            header: 'To',
            width: 200,
            render: (_, r) => r._line ? null : (
                <div>
                    <Text size="sm" fw={500}>{r.toWarehouseName}</Text>
                    <Text size="xs" c="dimmed">{r.toNodeName ?? 'not in a node'}{r.toFacilityCode ? ` · ${r.toFacilityCode}` : ''}</Text>
                </div>
            ),
        },
        { key: 'skuCount', header: 'SKUs', width: 70, align: 'right', render: (v: number, r) => r._line ? null : formatCount(v) },
        { key: 'units', header: 'Units', width: 90, align: 'right', render: (v: number, r) => formatCount((r._line ? r._line.qty : v)) },
        { key: 'cbm', header: 'CBM', width: 80, align: 'right', render: (v: number | null, r) => { const x = r._line ? r._line.cbm : v; return x == null ? '—' : x.toFixed(2); } },
        {
            key: '_instruments',
            header: 'Creates',
            width: 260,
            render: (_, r) => r._line ? null : (
                <Group gap={6} wrap="wrap">
                    <Badge color="violet" variant="light" size="xs">STN</Badge>
                    {pushToWms && r.gatePassPossible && <Badge color="indigo" variant="light" size="xs">Uniware gate pass · STOCK_TRANSFER</Badge>}
                    {pushToWms && !r.gatePassPossible && r.gatePassBlocker && (
                        <Tooltip label={r.gatePassBlocker} multiline w={320}>
                            <Badge color="orange" variant="light" size="xs">no gate pass</Badge>
                        </Tooltip>
                    )}
                </Group>
            ),
        },
    ];
    const blocked = preview?.routes.filter(r => !r.gatePassPossible).length ?? 0;
    return (
        <Modal opened onClose={onCancel} title={<Text fw={600} size="sm">Review what approval will create</Text>} size="xl" radius="md" centered>
            {loading || !preview ? (
                <Stack align="center" p="lg"><Loader size="sm" color="violet" /></Stack>
            ) : (
                <Stack gap="sm">
                    <Text size="sm">
                        <strong>{preview.transferCount}</strong> transfer{preview.transferCount !== 1 ? 's' : ''} become{preview.transferCount === 1 ? 's' : ''}{' '}
                        <strong>{preview.stnCount}</strong> stock transfer note{preview.stnCount !== 1 ? 's' : ''} — one per warehouse pair, one line per SKU —{' '}
                        moving <strong>{formatCount(preview.totalUnits)}</strong> units. Expand a row to see its SKU lines.
                    </Text>
                    {preview.wmsPushAvailable ? (
                        <Checkbox
                            size="sm"
                            color="violet"
                            checked={pushToWms}
                            onChange={e => onPushToWmsChange(e.currentTarget.checked)}
                            label="Also create one STOCK_TRANSFER gate pass in Uniware per STN"
                            description={`The gate pass carries our STN code, so the daily sync writes dispatch and receipt back onto it.${blocked > 0 && pushToWms ? ` ${blocked} route${blocked !== 1 ? 's' : ''} cannot get a gate pass — hover the badge for why; the STN is still created.` : ''}`}
                        />
                    ) : (
                        <Text size="xs" c="orange.8">
                            No WMS push: {preview.wmsPushBlocker}
                        </Text>
                    )}
                    <DataSheet
                        data={rows}
                        columns={columns}
                        getRowKey={r => r._rowKey}
                        getSubRows={linesOf}
                        bare
                        hideHeaderBar
                        maxHeight="50vh"
                        emptyState={<Text size="sm" c="dimmed" p="md">Nothing to raise: no leg has a quantity.</Text>}
                    />
                    <Group justify="flex-end" mt="xs">
                        <Button variant="subtle" color="gray" size="sm" onClick={onCancel}>Cancel</Button>
                        <Button color="violet" size="sm" leftSection={<IconCheck size={14} />} loading={approving} disabled={preview.stnCount === 0} onClick={onApprove}>
                            Approve · create {preview.stnCount} STN{preview.stnCount !== 1 ? 's' : ''}{preview.wmsPushAvailable && pushToWms ? ` + ${preview.routes.filter(r => r.gatePassPossible).length} gate pass${preview.routes.filter(r => r.gatePassPossible).length !== 1 ? 'es' : ''}` : ''}
                        </Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    );
};

// ─── Plan list ─────────────────────────────────────────────────────────────────

const PlanList: FC<{ onNew: () => void; onView: (plan: RebalancePlan) => void }> = ({ onNew, onView }) => {
    const [plans, setPlans] = useState<RebalancePlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    useEffect(() => {
        getRebalancePlans(50, 0)
            .then(r => setPlans(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleDelete = (plan: RebalancePlan, e: React.MouseEvent) => {
        e.stopPropagation();
        modals.openConfirmModal({
            title: 'Delete plan',
            children: (
                <Text size="sm">
                    Delete <strong>{plan.name}</strong>? This will also delete all stock transfer notes created against it. This cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setDeletingId(plan.id);
                try {
                    await deleteRebalancePlan(plan.id);
                    setPlans(prev => prev.filter(p => p.id !== plan.id));
                    showSuccess(`Plan "${plan.name}" deleted`);
                } catch {
                    showError('Failed to delete plan. Please try again.');
                } finally {
                    setDeletingId(null);
                }
            },
        });
    };

    if (loading) {
        return (
            <Stack align="center" justify="center" style={{ flex: 1 }} gap="sm">
                <Loader size="sm" color="violet" />
            </Stack>
        );
    }

    if (plans.length === 0) {
        return (
            <Stack align="center" justify="center" style={{ flex: 1 }} gap="md">
                <Text size="md" fw={600} c="dimmed">No rebalance plans yet</Text>
                <Text size="sm" c="dimmed" ta="center" maw={340}>
                    Create your first plan to optimise stock distribution across warehouses.
                </Text>
                <Button
                    color="violet"
                    radius="md"
                    leftSection={<IconPlus size={16} />}
                    onClick={onNew}
                >
                    Create your first plan
                </Button>
            </Stack>
        );
    }

    return (
        <Stack gap="md">
            <Group justify="space-between">
                <Text size="md" fw={600}>Rebalance Plans</Text>
                <Button
                    color="violet"
                    size="sm"
                    radius="md"
                    onClick={onNew}
                    rightSection={<IconArrowRight size={16} />}
                >
                    New Plan
                </Button>
            </Group>

            <Stack gap="xs">
                {plans.map(plan => (
                    <Card
                        key={plan.id}
                        withBorder
                        radius="md"
                        p="sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => onView(plan)}
                    >
                        <Group justify="space-between">
                            <div>
                                <Text fw={600} size="sm">{plan.name}</Text>
                                <Text size="xs" c="dimmed" mt={2}>
                                    {new Date(plan.planDate).toLocaleDateString()} ·{' '}
                                    {plan.recommendedTransfers?.filter(t => t.status !== 'REJECTED').length ?? 0} transfers
                                </Text>
                            </div>
                            <Group gap="xs">
                                <Badge color={statusColor(plan.status)} variant="light" size="sm">
                                    {plan.status}
                                </Badge>
                                {isPlanStale(plan) && (
                                    <Badge
                                        color="orange"
                                        variant="light"
                                        size="sm"
                                        leftSection={<IconAlertTriangle size={10} />}
                                    >
                                        Outdated
                                    </Badge>
                                )}
                                <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    size="sm"
                                    loading={deletingId === plan.id}
                                    onClick={e => handleDelete(plan, e)}
                                    title="Delete plan"
                                >
                                    <IconTrash size={14} />
                                </ActionIcon>
                                <IconArrowRight size={16} color="var(--mantine-color-gray-5)" />
                            </Group>
                        </Group>
                    </Card>
                ))}
            </Stack>
        </Stack>
    );
};

// ─── Plan view types ───────────────────────────────────────────────────────────

type PivotedRow = {
    productId: string;
    skuCode: string | null;
    productName: string | null;
    category: string | null;
    tier: string | null;
    productGroup: string | null;
    isActive: boolean | null;
    /** true = in productHealth but no recommended transfer for this toWH */
    isAvailableOnly: boolean;
    transfers: Record<string, RecommendedTransfer>; // fromWarehouseId → transfer
};

type SkuVisibility = 'all' | 'recommended' | 'available';

// ─── Plan view (split: settings + datasheet) ───────────────────────────────────

const PlanView: FC<{
    plan: RebalancePlan | null;
    initialScope?: RebalanceScope | null;
    /** New plan opened from a Home plan card: compute on arrival instead of waiting for the button. */
    autoCompute?: boolean;
    onBack: () => void;
    onPlanUpdated: (plan: RebalancePlan) => void;
    onDeleted: () => void;
}> = ({ plan: planProp, initialScope, autoCompute = false, onBack, onPlanUpdated, onDeleted }) => {
    // Fetch a fresh copy by ID on mount so the plan view always has full enriched data,
    // not the potentially stale snapshot from the list endpoint.
    const [loadedPlan, setLoadedPlan] = useState<RebalancePlan | null>(planProp);
    const [planLoading, setPlanLoading] = useState(planProp !== null);

    useEffect(() => {
        if (planProp === null) {
            setLoadedPlan(null);
            setPlanLoading(false);
            return;
        }
        setPlanLoading(true);
        getRebalancePlan(planProp.id)
            .then(fresh => {
                setLoadedPlan(fresh);
                onPlanUpdated(fresh);
            })
            .catch(() => setLoadedPlan(planProp))
            .finally(() => setPlanLoading(false));
    // Only re-fetch when the plan ID changes (i.e. user navigated to a different plan)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planProp?.id]);

    const plan = loadedPlan;

    const isNew = plan === null;
    const isReadOnly = !isNew && plan.status !== 'DRAFT';
    const isApproved = !isNew && plan.status !== 'DRAFT';

    const [computing, setComputing] = useState(false);
    const [computeError, setComputeError] = useState<string | null>(null);

    // Scope: what the optimizer treats as a node. Preset from the overview for a new plan;
    // read back from the plan's parameters once it exists.
    const [scope, setScope] = useState<RebalanceScope>(initialScope ?? { level: 'WAREHOUSE' });
    const [nodeList, setNodeList] = useState<RebalanceNode[]>([]);
    const [lanes, setLanes] = useState<RebalanceWarehousePath[]>([]);
    const [legEditorFor, setLegEditorFor] = useState<RecommendedTransfer | null>(null);
    useEffect(() => {
        getRebalanceNodes().then(r => setNodeList(r.nodes.filter(n => n.isActive))).catch(() => setNodeList([]));
        getRebalancePaths().then(setLanes).catch(() => setLanes([]));
    }, []);
    const planScope = planScopeOf(plan);
    const isNetwork = planScope.level === 'NETWORK';

    // WMS: gate passes are only offered when the org's Unicommerce STN push policy allows them.
    const [wmsPushAllowed, setWmsPushAllowed] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewPreview, setReviewPreview] = useState<ApprovalPreview | null>(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [pushToWms, setPushToWms] = useState(true);
    const [pushingWms, setPushingWms] = useState(false);
    useEffect(() => {
        getOrgPolicies()
            .then(p => setWmsPushAllowed(p.wmsType === 'unicommerce' && p.pushPolicy?.stn === 'GATE_PASS'))
            .catch(() => setWmsPushAllowed(false));
    }, []);
    // Node membership per node key, for the per-warehouse split in the pivot view.
    const membersOfNode = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const n of plan?.nodeHealth ?? []) if (!n.isSingleton) map.set(n.nodeKey, n.memberWarehouseIds);
        return map;
    }, [plan?.nodeHealth]);
    /** Destination members of a node in receive-rank order (falls back to health order). */
    const destMembersOf = (nodeKey: string): string[] => {
        const node = nodeList.find(n => n.id === nodeKey);
        if (node) return [...node.members].sort((a, b) => a.destRank - b.destRank).map(m => m.warehouseId);
        return membersOfNode.get(nodeKey) ?? [nodeKey];
    };
    const laneExists = (from: string, to: string) =>
        lanes.some(l => l.isActive && l.fromWarehouseId === from && l.toWarehouseId === to);
    /** Sum of legs leaving one source warehouse for a transfer, after local edits. */
    const legQtyFrom = (transfer: RecommendedTransfer, fromWh: string): number => {
        const edit = localLegEdits.get(`${transfer.id}|${fromWh}`);
        if (edit !== undefined) return edit;
        return (transfer.legs ?? []).filter(l => l.fromWarehouseId === fromWh).reduce((a, l) => a + l.qty, 0);
    };
    /**
     * Rebuild a transfer's legs from per-source edits: an edited source keeps its existing
     * destination (or gets the best-ranked destination it has a lane to); untouched sources keep
     * their legs as they are. The server validates every lane and re-sums qtyApproved.
     */
    const buildLegEditsPayload = (): Record<string, TransferLeg[]> => {
        const byTransfer = new Map<string, Map<string, number>>();
        for (const [key, qty] of localLegEdits) {
            const [transferId, fromWh] = key.split('|');
            if (!byTransfer.has(transferId)) byTransfer.set(transferId, new Map());
            byTransfer.get(transferId)!.set(fromWh, qty);
        }
        const payload: Record<string, TransferLeg[]> = {};
        for (const [transferId, edits] of byTransfer) {
            const transfer = allTransfers.find(t => t.id === transferId);
            if (!transfer) continue;
            const legs: TransferLeg[] = [];
            for (const leg of transfer.legs ?? []) {
                if (!edits.has(leg.fromWarehouseId)) legs.push({ ...leg });
            }
            for (const [fromWh, qty] of edits) {
                if (qty <= 0) continue;
                const existing = (transfer.legs ?? []).find(l => l.fromWarehouseId === fromWh);
                const toWh = existing?.toWarehouseId
                    ?? destMembersOf(transfer.toWarehouseId).find(d => laneExists(fromWh, d))
                    ?? destMembersOf(transfer.toWarehouseId)[0]
                    ?? transfer.toWarehouseId;
                legs.push({
                    legId: existing?.legId ?? `new-${fromWh}-${Date.now()}`,
                    pathId: existing?.pathId ?? '',
                    fromWarehouseId: fromWh,
                    toWarehouseId: toWh,
                    qty,
                    leadTimeDays: existing?.leadTimeDays ?? 0,
                    costPerUnit: existing?.costPerUnit ?? null,
                });
            }
            payload[transferId] = legs;
        }
        return payload;
    };

    const [editingName, setEditingName] = useState(false);
    const [nameValue, setNameValue] = useState(plan?.name ?? `Rebalance ${new Date().toLocaleDateString()}`);
    const [bulkOverrideOpen, setBulkOverrideOpen] = useState(false);

    const [localQtyOverrides, setLocalQtyOverrides] = useState<Map<string, number>>(new Map());
    const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);

    const [bulkOverridePhase, setBulkOverridePhase] = useState<'idle' | 'done'>('idle');
    const [bulkOverrideCount, setBulkOverrideCount] = useState(0);

    const [selectedToWh, setSelectedToWh] = useState<string | null>(null);
    const [focusedInputId, setFocusedInputId] = useState<string | null>(null);
    const [inputDraftValues, setInputDraftValues] = useState<Map<string, string>>(new Map());
    const [savingEdits, setSavingEdits] = useState(false);
    const [newTransferDrafts, setNewTransferDrafts] = useState<Map<string, number>>(new Map());
    /** Network pivot: per-source-warehouse leg quantity edits, keyed `${transferId}|${fromWarehouseId}`. */
    const [localLegEdits, setLocalLegEdits] = useState<Map<string, number>>(new Map());

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [routeFilter, setRouteFilter] = useState<FilterValue>({});
    const [productFilter, setProductFilter] = useState<FilterValue>({});
    const [skuVisibility, setSkuVisibility] = useState<SkuVisibility>('all');

    // STNs for approved plans
    const [stns, setStns] = useState<RebalancePlanTransfer[]>([]);
    const [stnsLoading, setStnsLoading] = useState(false);

    const allTransfers: RecommendedTransfer[] = plan?.recommendedTransfers ?? [];

    // Names for both physical warehouses and node keys (a network plan's from/to are node ids).
    const warehouseNameMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const wh of plan?.warehouseHealth ?? []) {
            map.set(wh.warehouseId, wh.warehouseName ?? wh.warehouseId);
        }
        for (const n of plan?.nodeHealth ?? []) {
            map.set(n.nodeKey, n.warehouseName ?? n.nodeKey);
        }
        for (const n of nodeList) map.set(n.id, n.name);
        return map;
    }, [plan, nodeList]);
    const nameOf = (id: string) => warehouseNameMap.get(id) ?? id;

    const productHealthMap = useMemo(() => {
        const map = new Map<string, ProductHealthSnapshot>();
        for (const ph of plan?.productHealth ?? []) {
            map.set(`${ph.warehouseId}|${ph.productId}`, ph);
        }
        return map;
    }, [plan]);

    // Per-unit volume (CBM) keyed by productId, sourced from the enriched transfers.
    // Used to total transfer volume in the aggregate rows.
    const productVolumeMap = useMemo(() => {
        const map = new Map<string, number>();
        for (const t of allTransfers) {
            if (t.volumeCbm != null && !map.has(t.productId)) map.set(t.productId, t.volumeCbm);
        }
        return map;
    }, [allTransfers]);

    useEffect(() => {
        setSearchQuery('');
        setSelectedToWh(null);
        setSkuVisibility('all');
        setLocalQtyOverrides(new Map());
        setInputDraftValues(new Map());
        setNewTransferDrafts(new Map());
        setLocalLegEdits(new Map());
        if (allTransfers.length) {
            setApprovedIds(new Set(
                allTransfers
                    .filter(t => t.status !== 'REJECTED' && !t.raised)
                    .map(t => t.id),
            ));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan?.id]);

    useEffect(() => {
        if (!plan || (plan.status === 'DRAFT' && !(plan.stnIds?.length))) return;
        setStnsLoading(true);
        getRebalanceTransfers({ planId: plan.id })
            .then(setStns)
            .catch(() => {})
            .finally(() => setStnsLoading(false));
    }, [plan?.id, plan?.status, plan?.stnIds?.length]);

    // ── Filter options ──
    const routeOptions = useMemo(() => {
        const unique = [...new Set(allTransfers.map(t => `${t.fromWarehouseId} → ${t.toWarehouseId}`))].sort();
        return unique.map(r => {
            const [from, to] = r.split(' → ');
            return { label: `${warehouseNameMap.get(from) ?? from} → ${warehouseNameMap.get(to) ?? to}`, value: r };
        });
    }, [allTransfers, warehouseNameMap]);

    const categoryOptions = useMemo(() => {
        const unique = [...new Set(allTransfers.map(t => t.category).filter((c): c is string => Boolean(c)))].sort();
        return unique.map(c => ({ label: c, value: c }));
    }, [allTransfers]);

    const productGroupOptions = useMemo(() => {
        const unique = [...new Set(allTransfers.map(t => t.productGroup).filter((g): g is string => Boolean(g)))].sort();
        return unique.map(g => ({ label: g, value: g }));
    }, [allTransfers]);

    const tierOptions = useMemo(() => {
        const unique = [...new Set(allTransfers.map(t => t.tier).filter((t): t is string => Boolean(t)))].sort();
        return unique.map(t => ({ label: t, value: t }));
    }, [allTransfers]);

    const { params: productFilterParams, resolvedAttrIds: rebalanceResolvedAttrIds } = useProductFilterParams(
        { categoryOptions, productGroupOptions, tierOptions },
        productFilter,
    );

    const selectedRoutes = routeFilter.route ?? [];
    const selectedCategories = productFilter.category ?? [];
    const selectedProductGroups = productFilter.productGroup ?? [];
    const selectedTiers = productFilter.tier ?? [];
    const selectedIsActive = productFilter.isActive;

    const filteredTransfers = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const filtered = allTransfers.filter(t => {
            if (selectedToWh && t.toWarehouseId !== selectedToWh) return false;
            if (selectedRoutes.length > 0 && !selectedRoutes.includes(`${t.fromWarehouseId} → ${t.toWarehouseId}`)) return false;
            if (selectedCategories.length > 0 && !selectedCategories.includes(t.category ?? '')) return false;
            if (selectedProductGroups.length > 0 && !selectedProductGroups.includes(t.productGroup ?? '')) return false;
            if (selectedTiers.length > 0 && !selectedTiers.includes(t.tier ?? '')) return false;
            if (selectedIsActive?.length) {
                const wantActive = selectedIsActive[0] === 'true';
                if (t.isActive !== wantActive) return false;
            }
            if (rebalanceResolvedAttrIds && !rebalanceResolvedAttrIds.has(t.productId)) return false;
            if (q) {
                const matchesSku = (t.skuCode ?? '').toLowerCase().includes(q);
                const matchesName = (t.productName ?? '').toLowerCase().includes(q);
                const matchesId = t.productId.toLowerCase().includes(q);
                if (!matchesSku && !matchesName && !matchesId) return false;
            }
            return true;
        });
        // Flat view: sort by route (fromWH → toWH), then SKU within each route
        if (!selectedToWh) {
            filtered.sort((a, b) => {
                const routeA = `${a.fromWarehouseId}|${a.toWarehouseId}`;
                const routeB = `${b.fromWarehouseId}|${b.toWarehouseId}`;
                if (routeA !== routeB) return routeA.localeCompare(routeB);
                return (a.skuCode ?? a.productId).localeCompare(b.skuCode ?? b.productId);
            });
        }
        return filtered;
    }, [allTransfers, searchQuery, selectedRoutes, selectedCategories, selectedProductGroups, selectedTiers, selectedIsActive, rebalanceResolvedAttrIds, selectedToWh]);

    // Geo node of each physical warehouse, in every scope — the pivot files warehouses under it.
    const nodeOfWarehouse = useMemo(() => {
        const map = new Map<string, { nodeId: string; nodeName: string }>();
        for (const wh of plan?.warehouseHealth ?? []) {
            if (wh.nodeId) map.set(wh.warehouseId, { nodeId: wh.nodeId, nodeName: wh.nodeName ?? wh.nodeId });
        }
        return map;
    }, [plan?.warehouseHealth]);
    const nodeLabelOf = (whId: string): string | null => {
        if (plan?.nodeHealth?.some(n => n.nodeKey === whId && !n.isSingleton)) return warehouseNameMap.get(whId) ?? whId;
        return nodeOfWarehouse.get(whId)?.nodeName ?? null;
    };

    const toWhOptions = useMemo(() => {
        const unique = [...new Set(allTransfers.map(t => t.toWarehouseId))];
        const byNode = new Map<string, { value: string; label: string }[]>();
        for (const id of unique) {
            const node = nodeLabelOf(id);
            const key = node ? (plan?.nodeHealth?.some(n => n.nodeKey === id) ? 'Nodes' : node) : 'Not in a node';
            if (!byNode.has(key)) byNode.set(key, []);
            byNode.get(key)!.push({ value: id, label: warehouseNameMap.get(id) ?? id });
        }
        if (byNode.size <= 1 && !byNode.has('Nodes')) {
            return unique.map(id => ({ value: id, label: warehouseNameMap.get(id) ?? id })).sort((a, b) => a.label.localeCompare(b.label));
        }
        return [...byNode.entries()]
            .sort(([a], [b]) => (a === 'Not in a node' ? 1 : b === 'Not in a node' ? -1 : a.localeCompare(b)))
            .map(([group, items]) => ({ group, items: items.sort((a, b) => a.label.localeCompare(b.label)) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allTransfers, warehouseNameMap, nodeOfWarehouse, plan?.nodeHealth]);

    // Sources for the pivot, filed by node so neighbouring columns share a super group.
    const fromWhIds = useMemo(() => {
        if (!selectedToWh) return [];
        const ids = [...new Set(filteredTransfers.map(t => t.fromWarehouseId))];
        const sortKey = (id: string) => `${nodeLabelOf(id) ?? '~'}|${warehouseNameMap.get(id) ?? id}`;
        return ids.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredTransfers, selectedToWh, nodeOfWarehouse, warehouseNameMap]);

    const pivotedRows = useMemo<PivotedRow[]>(() => {
        if (!selectedToWh) return [];

        // Recommended rows — products with at least one transfer to this toWH
        const byProduct = new Map<string, RecommendedTransfer[]>();
        for (const t of filteredTransfers) {
            const arr = byProduct.get(t.productId) ?? [];
            arr.push(t);
            byProduct.set(t.productId, arr);
        }
        const recommendedRows: PivotedRow[] = [...byProduct.entries()].map(([productId, tList]) => ({
            productId,
            skuCode: tList[0]?.skuCode ?? null,
            productName: tList[0]?.productName ?? null,
            category: tList[0]?.category ?? null,
            tier: tList[0]?.tier ?? null,
            productGroup: tList[0]?.productGroup ?? null,
            isActive: tList[0]?.isActive ?? null,
            isAvailableOnly: false,
            transfers: Object.fromEntries(tList.map(t => [t.fromWarehouseId, t])),
        }));

        const recommendedProductIds = new Set(byProduct.keys());

        // Available-only rows — active products in productHealth for this toWH not in any transfer.
        // isActive === true: excludes deactivated products that may still have physical stock.
        // The stock/demand > 0 guard removes ghost entries from the optimizer's product universe.
        const q = searchQuery.trim().toLowerCase();
        const availableRows: PivotedRow[] = (plan?.productHealth ?? [])
            .filter(ph =>
                ph.warehouseId === selectedToWh &&
                !recommendedProductIds.has(ph.productId) &&
                ph.isActive === true &&
                (ph.currentStockUnits > 0 || ph.inTransitUnits > 0 || ph.targetDemandUnits > 0),
            )
            .filter((ph, idx, arr) => arr.findIndex(x => x.productId === ph.productId) === idx)
            .filter(ph => {
                if (selectedCategories.length > 0 && !selectedCategories.includes(ph.category ?? '')) return false;
                if (selectedTiers.length > 0 && !selectedTiers.includes(ph.tier ?? '')) return false;
                if (selectedProductGroups.length > 0 && !selectedProductGroups.includes(ph.productGroup ?? '')) return false;
                if (selectedIsActive?.length) {
                    const wantActive = selectedIsActive[0] === 'true';
                    if (ph.isActive !== wantActive) return false;
                }
                if (rebalanceResolvedAttrIds && !rebalanceResolvedAttrIds.has(ph.productId)) return false;
                if (q) {
                    const matchesSku = (ph.skuCode ?? '').toLowerCase().includes(q);
                    const matchesName = (ph.productName ?? '').toLowerCase().includes(q);
                    const matchesId = ph.productId.toLowerCase().includes(q);
                    if (!matchesSku && !matchesName && !matchesId) return false;
                }
                return true;
            })
            .map(ph => ({
                productId: ph.productId,
                skuCode: ph.skuCode ?? null,
                productName: ph.productName ?? null,
                category: ph.category ?? null,
                tier: ph.tier ?? null,
                productGroup: ph.productGroup ?? null,
                isActive: ph.isActive ?? null,
                isAvailableOnly: true,
                transfers: {},
            }));

        const combined = [...recommendedRows, ...availableRows];

        if (skuVisibility === 'recommended') return combined.filter(r => !r.isAvailableOnly);
        if (skuVisibility === 'available') return combined.filter(r => r.isAvailableOnly);
        return combined;
    }, [filteredTransfers, selectedToWh, plan?.productHealth, skuVisibility, searchQuery,
        selectedCategories, selectedTiers, selectedProductGroups, selectedIsActive, rebalanceResolvedAttrIds]);

    const rowIndexMap = useMemo(
        () => new Map(filteredTransfers.map((t, i) => [t.id, i])),
        [filteredTransfers],
    );

    // Approval acts on what is on screen: the pivot's rows when a To warehouse is selected,
    // the filtered flat rows otherwise — and never on a transfer that is already an STN.
    const visibleTransferIds = useMemo(() => {
        if (selectedToWh) return new Set(pivotedRows.flatMap(r => Object.values(r.transfers).map(t => t.id)));
        return new Set(filteredTransfers.map(t => t.id));
    }, [selectedToWh, pivotedRows, filteredTransfers]);
    const scopedApprovedIds = useMemo(() => {
        const raised = new Set(allTransfers.filter(t => t.raised).map(t => t.id));
        return new Set([...approvedIds].filter(id => visibleTransferIds.has(id) && !raised.has(id)));
    }, [approvedIds, visibleTransferIds, allTransfers]);
    const raisedCount = allTransfers.filter(t => t.raised).length;
    const isRaised = (t: RecommendedTransfer) => Boolean(t.raised);

    const uploadRef = useRef<HTMLInputElement>(null);

    const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const text = ev.target?.result as string;
            const lines = text.trim().split('\n');
            if (lines.length < 2) { showError('CSV is empty'); return; }

            const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
            const idIdx = headers.findIndex(h => h.includes('transfer id') || h === 'id');
            const skuIdx = headers.findIndex(h => h.includes('sku'));
            const fromIdx = headers.findIndex(h => h.includes('from'));
            const toIdx = headers.findIndex(h => h.includes('to'));
            const qtyIdx = headers.findIndex(h => h.includes('approved'));

            if (qtyIdx === -1) { showError('CSV must have an "Approved Qty" column'); return; }

            const byId = new Map(allTransfers.map(t => [t.id, t]));
            const byPath = new Map(allTransfers.map(t => [`${t.productId}|${t.fromWarehouseId}|${t.toWarehouseId ?? ''}`, t]));

            const newOverrides = new Map(localQtyOverrides);
            let updated = 0;

            for (let i = 1; i < lines.length; i++) {
                const cols = lines[i].split(',').map(c => c.replace(/"/g, '').trim());
                const qty = Math.ceil(Number(cols[qtyIdx]));
                if (!cols[qtyIdx] || isNaN(qty) || qty <= 0) continue;

                let transfer = idIdx !== -1 ? byId.get(cols[idIdx]) : undefined;
                if (!transfer && skuIdx !== -1 && fromIdx !== -1 && toIdx !== -1) {
                    transfer = byPath.get(`${cols[skuIdx]}|${cols[fromIdx]}|${cols[toIdx]}`);
                }
                if (transfer) { newOverrides.set(transfer.id, qty); updated++; }
            }

            setLocalQtyOverrides(newOverrides);
            setBulkOverrideCount(updated);
            setBulkOverridePhase('done');
            e.target.value = '';
        };
        reader.readAsText(file);
    };

    const dataSheetFilters: DataSheetFilterConfig[] = useMemo(() => [
        {
            label: 'All Routes',
            params: [{ key: 'route', label: 'Route', mode: 'single', options: routeOptions }],
            value: routeFilter,
            onChange: setRouteFilter,
        },
        {
            label: 'Product',
            params: productFilterParams,
            value: productFilter,
            onChange: setProductFilter,
        },
    ], [routeOptions, productFilterParams, routeFilter, productFilter]);

    // Pivot view uses only the product filter — route filter is irrelevant when a toWH is selected
    const pivotDataSheetFilters: DataSheetFilterConfig[] = useMemo(() => [
        {
            label: 'Product',
            params: productFilterParams,
            value: productFilter,
            onChange: setProductFilter,
        },
    ], [productFilterParams, productFilter]);

    const canApprove = !isNew && plan?.status === 'DRAFT';
    const hasTransfers = allTransfers.length > 0;
    const hasRaised = allTransfers.some(t => t.raised);
    const showStatusCol = !isNew && (plan.status !== 'DRAFT' || hasRaised);
    const showStns = isApproved || (plan?.stnIds?.length ?? 0) > 0;

    // ── Column sums (all filtered transfers, not just the current page) ──
    const aggregateRow = useMemo<Partial<Record<string, ReactNode>>>(() => {
        if (!filteredTransfers.length) return {};
        let fromStock = 0, fromIT = 0, fromDemand = 0;
        let toStock = 0, toIT = 0, toDemand = 0;
        let qty = 0, cbm = 0;
        for (const t of filteredTransfers) {
            const fp = productHealthMap.get(`${t.fromWarehouseId}|${t.productId}`);
            const tp = productHealthMap.get(`${t.toWarehouseId}|${t.productId}`);
            fromStock += fp?.currentStockUnits ?? 0;
            fromIT += fp?.inTransitUnits ?? 0;
            fromDemand += fp?.targetDemandUnits ?? 0;
            toStock += tp?.currentStockUnits ?? 0;
            toIT += tp?.inTransitUnits ?? 0;
            toDemand += tp?.targetDemandUnits ?? 0;
            const q = localQtyOverrides.get(t.id) ?? t.qtyApproved ?? t.qtyRecommended;
            qty += q;
            cbm += q * (productVolumeMap.get(t.productId) ?? 0);
        }
        return {
            _fromStock: formatCount(fromStock),
            _fromInTransit: formatCount(fromIT),
            _fromDemand: formatCount(Math.round(fromDemand)),
            _toStock: formatCount(toStock),
            _toInTransit: formatCount(toIT),
            _toDemand: formatCount(Math.round(toDemand)),
            qtyApproved: renderQtyWithCbm(qty, cbm),
        };
    }, [filteredTransfers, productHealthMap, localQtyOverrides, productVolumeMap]);

    // Pivot view: per-destination-warehouse totals, with each From-WH transfer
    // column showing total units + total CBM (mirrors the flat view's aggregate).
    const pivotAggregateRow = useMemo<Partial<Record<string, ReactNode>>>(() => {
        if (!selectedToWh || pivotedRows.length === 0) return {};
        const result: Partial<Record<string, ReactNode>> = {};

        let toStock = 0, toIT = 0, toDemand = 0;
        for (const row of pivotedRows) {
            const ph = productHealthMap.get(`${selectedToWh}|${row.productId}`);
            toStock += ph?.currentStockUnits ?? 0;
            toIT += ph?.inTransitUnits ?? 0;
            toDemand += ph?.targetDemandUnits ?? 0;
        }
        result._toStock = formatCount(toStock);
        result._toIT = formatCount(toIT);
        result._toDemand = formatCount(Math.round(toDemand));

        for (const fromWhId of fromWhIds) {
            let stock = 0, it = 0, demand = 0, qty = 0, cbm = 0;
            for (const row of pivotedRows) {
                const ph = productHealthMap.get(`${fromWhId}|${row.productId}`);
                stock += ph?.currentStockUnits ?? 0;
                it += ph?.inTransitUnits ?? 0;
                demand += ph?.targetDemandUnits ?? 0;
                const transfer = row.transfers[fromWhId];
                const q = transfer
                    ? (localQtyOverrides.get(transfer.id) ?? transfer.qtyApproved ?? transfer.qtyRecommended)
                    : (newTransferDrafts.get(`${fromWhId}|${selectedToWh}|${row.productId}`) ?? 0);
                qty += q;
                cbm += q * (productVolumeMap.get(row.productId) ?? 0);
            }
            result[`_${fromWhId}_stock`] = formatCount(stock);
            result[`_${fromWhId}_it`] = formatCount(it);
            result[`_${fromWhId}_demand`] = formatCount(Math.round(demand));
            result[`_${fromWhId}_qty`] = renderQtyWithCbm(qty, cbm);
            for (const memberId of membersOfNode.get(fromWhId) ?? []) {
                let mStock = 0, mIt = 0, mDemand = 0, mQty = 0;
                for (const row of pivotedRows) {
                    const ph = productHealthMap.get(`${memberId}|${row.productId}`);
                    mStock += ph?.currentStockUnits ?? 0;
                    mIt += ph?.inTransitUnits ?? 0;
                    mDemand += ph?.targetDemandUnits ?? 0;
                    const transfer = row.transfers[fromWhId];
                    if (transfer) mQty += legQtyFrom(transfer, memberId);
                }
                result[`_from_${memberId}_stock`] = formatCount(mStock);
                result[`_from_${memberId}_it`] = formatCount(mIt);
                result[`_from_${memberId}_demand`] = formatCount(Math.round(mDemand));
                result[`_${fromWhId}_${memberId}_qty`] = formatCount(mQty);
            }
        }
        return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedToWh, pivotedRows, fromWhIds, productHealthMap, localQtyOverrides, newTransferDrafts, productVolumeMap, localLegEdits, membersOfNode]);

    // ── Inline name edit ──
    const handleNameSave = async () => {
        setEditingName(false);
        const trimmed = nameValue.trim();
        if (!plan || !trimmed || trimmed === plan.name) return;
        try {
            const updated = await updateRebalancePlan(plan.id, { name: trimmed });
            onPlanUpdated(updated);
        } catch { /* silently ignore */ }
    };

    // ── Save qty edits ──
    const handleSave = async () => {
        if (!plan) return;
        setSavingEdits(true);
        setSaveError(null);
        try {
            const manualTransfers = [...newTransferDrafts.entries()].map(([key, qty]) => {
                const [fromWarehouseId, toWarehouseId, productId] = key.split('|');
                return { fromWarehouseId, toWarehouseId, productId, qty };
            });
            const legEdits = buildLegEditsPayload();
            const updated = await updateRebalancePlan(plan.id, {
                transferQtyEdits: Object.fromEntries(localQtyOverrides),
                ...(manualTransfers.length > 0 ? { manualTransfers } : {}),
                ...(Object.keys(legEdits).length > 0 ? { transferLegEdits: legEdits } : {}),
            });
            setLocalQtyOverrides(new Map());
            setNewTransferDrafts(new Map());
            setLocalLegEdits(new Map());
            const prevIds = new Set((plan.recommendedTransfers ?? []).map(t => t.id));
            const addedIds = (updated.recommendedTransfers ?? [])
                .filter(t => !prevIds.has(t.id))
                .map(t => t.id);
            if (addedIds.length > 0) {
                setApprovedIds(prev => new Set([...prev, ...addedIds]));
            }
            setLoadedPlan(updated);
            onPlanUpdated(updated);
        } catch {
            setSaveError('Failed to save edits. Please try again.');
        } finally {
            setSavingEdits(false);
        }
    };

    // ── Back with unsaved-edits guard ──
    const handleBack = () => {
        const unsavedCount = localQtyOverrides.size + newTransferDrafts.size + localLegEdits.size;
        if (unsavedCount > 0) {
            modals.openConfirmModal({
                title: 'Unsaved changes',
                children: (
                    <Text size="sm">
                        You have {unsavedCount} unsaved edit{unsavedCount !== 1 ? 's' : ''}. Leave without saving?
                    </Text>
                ),
                labels: { confirm: 'Discard & Leave', cancel: 'Stay' },
                confirmProps: { color: 'red' },
                onConfirm: onBack,
            });
        } else {
            onBack();
        }
    };

    // ── Compute (new plan) ──
    const handleCompute = async () => {
        setComputing(true);
        setComputeError(null);
        try {
            // One request: the backend runs the optimizer and freezes the result into the plan.
            const draft = await createRebalancePlan({
                name: nameValue || `Rebalance ${new Date().toLocaleDateString()}`,
                compute: { scope: { level: scope.level, nodeId: scope.nodeId ?? undefined } },
            });
            setLoadedPlan(draft);
            onPlanUpdated(draft);
        } catch (err: any) {
            const raw = err?.error?.message ?? err?.message;
            const text = Array.isArray(raw) ? raw.join('; ') : raw;
            setComputeError(text && String(text).trim() ? String(text) : 'Failed to compute plan. Check that routes are configured in Settings.');
        } finally {
            setComputing(false);
        }
    };

    // A plan card's deep link: run once on arrival, exactly as the button would.
    const autoRan = useRef(false);
    useEffect(() => {
        if (!autoCompute || planProp !== null || autoRan.current) return;
        autoRan.current = true;
        void handleCompute();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoCompute, planProp]);

    // ── Schedule this scope as a Home plan card ──
    const scopeKey = scope.level === 'NODE' ? `NODE:${scope.nodeId ?? ''}` : scope.level;
    const scopeName =
        scope.level === 'NODE'
            ? `within ${scope.nodeName ?? nodeList.find(n => n.id === scope.nodeId)?.name ?? scope.nodeId ?? 'node'}`
            : scope.level === 'NETWORK'
              ? 'across the network'
              : 'every warehouse';

    // ── Re-compute (existing DRAFT plan) ──
    const handleRecompute = () => {
        if (!plan) return;
        modals.openConfirmModal({
            title: 'Re-compute plan',
            children: <Text size="sm">This will replace all current transfers with a fresh computation using the latest rebalance settings. The plan name will be kept.</Text>,
            labels: { confirm: 'Re-compute', cancel: 'Cancel' },
            confirmProps: { color: 'violet' },
            onConfirm: async () => {
                setComputing(true);
                setComputeError(null);
                try {
                    const ps = planScopeOf(plan);
                    // One request: the backend re-runs the optimizer with the plan's scope and writes it back in place.
                    const updated = await updateRebalancePlan(plan.id, {
                        recompute: { scope: { level: ps.level, nodeId: ps.nodeId ?? undefined } },
                    });
                    // This view renders from its own copy of the plan (loaded by id), so a recompute
                    // has to replace that copy too — otherwise the old date and transfers stay on screen.
                    setLoadedPlan(updated);
                    setLocalQtyOverrides(new Map());
                    setNewTransferDrafts(new Map());
                    setLocalLegEdits(new Map());
                    setApprovedIds(new Set((updated.recommendedTransfers ?? []).filter(t => t.status !== 'REJECTED' && !t.raised).map(t => t.id)));
                    onPlanUpdated(updated);
                } catch {
                    setComputeError('Re-compute failed. Please try again.');
                } finally {
                    setComputing(false);
                }
            },
        });
    };

    // ── Approve: review the instruments first ──
    const handleApprove = async () => {
        if (!plan) return;
        setReviewOpen(true);
        setReviewLoading(true);
        setReviewPreview(null);
        try {
            // Unsaved edits are saved first so the preview reflects the quantities on screen.
            if (localQtyOverrides.size > 0 || newTransferDrafts.size > 0 || localLegEdits.size > 0) {
                await handleSave();
            }
            const preview = await getRebalanceApprovalPreview(plan.id, [...scopedApprovedIds]);
            setReviewPreview(preview);
            setPushToWms(preview.wmsPushAvailable);
        } catch {
            showError('Could not build the approval preview.');
            setReviewOpen(false);
        } finally {
            setReviewLoading(false);
        }
    };

    const doApprove = async () => {
        if (!plan) return;
        setSaving(true);
        setSaveError(null);
        try {
            // Auto-save any unsaved qty edits and manual transfers first
            if (localQtyOverrides.size > 0 || newTransferDrafts.size > 0 || localLegEdits.size > 0) {
                const manualTransfers = [...newTransferDrafts.entries()].map(([key, qty]) => {
                    const [fromWarehouseId, toWarehouseId, productId] = key.split('|');
                    return { fromWarehouseId, toWarehouseId, productId, qty };
                });
                const legEdits = buildLegEditsPayload();
                await updateRebalancePlan(plan.id, {
                    transferQtyEdits: Object.fromEntries(localQtyOverrides),
                    ...(manualTransfers.length > 0 ? { manualTransfers } : {}),
                    ...(Object.keys(legEdits).length > 0 ? { transferLegEdits: legEdits } : {}),
                });
                setLocalQtyOverrides(new Map());
                setNewTransferDrafts(new Map());
                setLocalLegEdits(new Map());
            }
            const approved = await approveRebalancePlan(plan.id, {
                approvedTransferIds: [...scopedApprovedIds],
                pushToWms: (reviewPreview?.wmsPushAvailable ?? wmsPushAllowed) && pushToWms,
            });
            setReviewOpen(false);
            setLoadedPlan(approved);
            onPlanUpdated(approved);
        } catch {
            setSaveError('Failed to approve plan. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        if (!plan) return;
        modals.openConfirmModal({
            title: 'Delete plan',
            children: (
                <Text size="sm">
                    Delete <strong>{plan.name}</strong>? This will also delete all stock transfer notes created against it. This cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setDeleting(true);
                try {
                    await deleteRebalancePlan(plan.id);
                    showSuccess(`Plan "${plan.name}" deleted`);
                    onDeleted();
                } catch {
                    showError('Failed to delete plan. Please try again.');
                    setDeleting(false);
                }
            },
        });
    };

    const pendingWmsCount = stns.filter(s => s.stnId && s.wmsStatus !== 'CREATED').length;
    const handlePushToWms = async () => {
        if (!plan) return;
        setPushingWms(true);
        try {
            const updated = await pushRebalancePlanToWms(plan.id);
            setStns(updated);
            const failed = updated.filter(s => s.wmsStatus === 'FAILED').length;
            if (failed > 0) showError(`${failed} gate pass${failed !== 1 ? 'es' : ''} failed — see the WMS column.`);
            else showSuccess('Gate passes created in Uniware');
        } catch (err: any) {
            const raw = err?.error?.message ?? err?.message;
            showError(Array.isArray(raw) ? raw.join('; ') : raw || 'Push failed.');
        } finally {
            setPushingWms(false);
        }
    };

    const toggleTransfer = (id: string) =>
        setApprovedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const toggleAll = () =>
        setApprovedIds(prev =>
            prev.size === allTransfers.length
                ? new Set()
                : new Set(allTransfers.map(t => t.id)),
        );

    // ── Transfer table columns ──
    const checkboxCol: DataSheetColumn<PlanRow> = {
        key: '_check',
        header: '',
        width: 40,
        sticky: true,
        sortable: false,
        filterable: false,
        renderHeader: () => (
            <Checkbox
                checked={approvedIds.size === allTransfers.length && allTransfers.length > 0}
                indeterminate={approvedIds.size > 0 && approvedIds.size < allTransfers.length}
                onChange={toggleAll}
                size="xs"
                color="violet"
            />
        ),
        render: (_, row) => row._isLeg ? null : isRaised(row) ? (
            <Tooltip label="Already raised as an STN"><Badge size="xs" variant="light" color="teal">STN</Badge></Tooltip>
        ) : (
            <Checkbox checked={approvedIds.has(row.id)} onChange={() => toggleTransfer(row.id)} size="xs" color="violet" />
        ),
    };

    // In a network plan each transfer expands to its physical legs.
    const legRowsOf = (row: PlanRow): PlanRow[] | undefined => {
        if (!isNetwork || row._isLeg || !row.legs || row.legs.length === 0) return undefined;
        return row.legs.map(leg => ({ ...row, id: leg.legId, _isLeg: true, _leg: leg }));
    };

    const columns: DataSheetColumn<PlanRow>[] = [
        {
            key: '_idx',
            header: '#',
            width: 44,
            sticky: true,
            render: (_, row) => row._isLeg
                ? <Text size="xs" c="dimmed">leg</Text>
                : <Text size="xs" c="dimmed">{(rowIndexMap.get(row.id) ?? 0) + 1}</Text>,
        },
        ...(canApprove ? [checkboxCol] : []),
        {
            key: 'productId',
            header: 'Product',
            width: 220,
            sticky: true,
            render: (_, row) => {
                if (row._isLeg) {
                    return <Text size="xs" c="dimmed">↳ {row._leg?.leadTimeDays ?? 0}d lane{row._leg?.costPerUnit != null ? ` · ${row._leg.costPerUnit}/unit` : ''}</Text>;
                }
                const name = row.productName || row.productId;
                const code = row.skuCode || row.productId;
                return (
                    <div>
                        <Text size="sm" fw={500}>{name}</Text>
                        {code !== name && <Text size="xs" c="dimmed">{code}</Text>}
                    </div>
                );
            },
        },
        {
            key: 'fromWarehouseId',
            header: isNetwork ? 'Node / warehouse' : 'Warehouse',
            width: 170,
            group: 'From',
            render: (_, row) => {
                const id = row._isLeg ? row._leg!.fromWarehouseId : row.fromWarehouseId;
                const isNode = !row._isLeg && Boolean(row.fromNodeId);
                return (
                    <Group gap={6} wrap="nowrap">
                        {isNode && <Badge size="xs" variant="light" color="indigo">NODE</Badge>}
                        <Text size="sm" fw={row._isLeg ? 400 : 500} truncate>{nameOf(id)}</Text>
                    </Group>
                );
            },
        },
        {
            key: '_fromStock',
            header: 'Current Stock',
            width: 120,
            align: 'right',
            group: 'From',
            render: (_, row) => {
                const id = row._isLeg ? row._leg!.fromWarehouseId : row.fromWarehouseId;
                const ph = productHealthMap.get(`${id}|${row.productId}`);
                return <Text size="sm">{ph ? formatCount(ph.currentStockUnits) : '—'}</Text>;
            },
        },
        {
            key: '_fromInTransit',
            header: 'In Transit',
            width: 110,
            align: 'right',
            group: 'From',
            render: (_, row) => {
                const id = row._isLeg ? row._leg!.fromWarehouseId : row.fromWarehouseId;
                const ph = productHealthMap.get(`${id}|${row.productId}`);
                return <Text size="sm">{ph ? formatCount(ph.inTransitUnits) : '—'}</Text>;
            },
        },
        {
            key: '_fromDemand',
            header: 'Target Demand',
            width: 130,
            align: 'right',
            group: 'From',
            render: (_, row) => {
                const id = row._isLeg ? row._leg!.fromWarehouseId : row.fromWarehouseId;
                const ph = productHealthMap.get(`${id}|${row.productId}`);
                return <Text size="sm">{ph ? formatCount(ph.targetDemandUnits) : '—'}</Text>;
            },
        },
        {
            key: 'toWarehouseId',
            header: isNetwork ? 'Node / warehouse' : 'Warehouse',
            width: 170,
            group: 'To',
            render: (_, row) => {
                const id = row._isLeg ? row._leg!.toWarehouseId : row.toWarehouseId;
                const isNode = !row._isLeg && Boolean(row.toNodeId);
                return (
                    <Group gap={6} wrap="nowrap">
                        {isNode && <Badge size="xs" variant="light" color="indigo">NODE</Badge>}
                        <Text size="sm" fw={row._isLeg ? 400 : 500} truncate>{nameOf(id)}</Text>
                    </Group>
                );
            },
        },
        {
            key: '_toStock',
            header: 'Current Stock',
            width: 120,
            align: 'right',
            group: 'To',
            render: (_, row) => {
                const id = row._isLeg ? row._leg!.toWarehouseId : row.toWarehouseId;
                const ph = productHealthMap.get(`${id}|${row.productId}`);
                return <Text size="sm">{ph ? formatCount(ph.currentStockUnits) : '—'}</Text>;
            },
        },
        {
            key: '_toInTransit',
            header: 'In Transit',
            width: 110,
            align: 'right',
            group: 'To',
            render: (_, row) => {
                const id = row._isLeg ? row._leg!.toWarehouseId : row.toWarehouseId;
                const ph = productHealthMap.get(`${id}|${row.productId}`);
                return <Text size="sm">{ph ? formatCount(ph.inTransitUnits) : '—'}</Text>;
            },
        },
        {
            key: '_toDemand',
            header: 'Target Demand',
            width: 130,
            align: 'right',
            group: 'To',
            render: (_, row) => {
                const id = row._isLeg ? row._leg!.toWarehouseId : row.toWarehouseId;
                const ph = productHealthMap.get(`${id}|${row.productId}`);
                return <Text size="sm">{ph ? formatCount(ph.targetDemandUnits) : '—'}</Text>;
            },
        },
        {
            key: 'qtyApproved',
            header: 'Transfer Qty',
            width: 130,
            align: 'right',
            group: 'Transfer',
            render: (_, row) => {
                if (row._isLeg) {
                    return <Text size="sm">{formatCount(row._leg!.qty)}</Text>;
                }
                if (canApprove && !isRaised(row)) {
                    const committedVal = localQtyOverrides.get(row.id) ?? row.qtyApproved ?? row.qtyRecommended;
                    const isOverridden = localQtyOverrides.has(row.id);
                    const draft = inputDraftValues.get(row.id);
                    const displayValue = draft !== undefined ? draft : String(committedVal ?? '');
                    return (
                        <div style={{ textAlign: 'right' }}>
                            <input
                                type="number"
                                value={displayValue}
                                min={0}
                                onChange={e => {
                                    const raw = e.target.value;
                                    setInputDraftValues(prev => new Map(prev).set(row.id, raw));
                                    const num = Math.ceil(Number(raw));
                                    if (raw !== '' && !isNaN(num) && num >= 0) {
                                        setLocalQtyOverrides(prev => new Map(prev).set(row.id, num));
                                    }
                                }}
                                onFocus={() => setFocusedInputId(row.id)}
                                onBlur={() => {
                                    setFocusedInputId(null);
                                    setInputDraftValues(prev => {
                                        const next = new Map(prev);
                                        next.delete(row.id);
                                        return next;
                                    });
                                }}
                                style={{
                                    width: 80,
                                    textAlign: 'right',
                                    border: 'none',
                                    borderBottom: `2px solid ${focusedInputId === row.id ? 'var(--mantine-color-indigo-6)' : 'var(--mantine-color-gray-4)'}`,
                                    background: 'transparent',
                                    outline: 'none',
                                    fontSize: 'var(--mantine-font-size-sm)',
                                    padding: '2px 4px',
                                    fontFamily: 'inherit',
                                }}
                            />
                            {isOverridden && (
                                <Text size="xs" c="dimmed" style={{ fontSize: 10, lineHeight: 1.2 }}>
                                    rec: {formatCount(row.qtyRecommended)}
                                </Text>
                            )}
                        </div>
                    );
                }
                const displayQty = row.qtyApproved ?? row.qtyRecommended;
                return (
                    <Text size="sm">
                        {displayQty != null ? formatCount(displayQty) : '—'}
                    </Text>
                );
            },
        },
        ...(isNetwork ? [{
            key: '_legs',
            header: 'Legs',
            width: 150,
            group: 'Transfer',
            render: (_: any, row: PlanRow) => {
                if (row._isLeg) return null;
                const count = row.legs?.length ?? 0;
                return (
                    <Group gap={6} wrap="nowrap">
                        <Text size="xs" c={count === 0 ? 'red' : 'dimmed'}>{count} leg{count !== 1 ? 's' : ''}</Text>
                        {canApprove && (
                            <Button size="compact-xs" variant="subtle" onClick={() => setLegEditorFor(row)}>Edit</Button>
                        )}
                    </Group>
                );
            },
        } as DataSheetColumn<PlanRow>] : []),
        {
            key: '_flags',
            header: 'Flags',
            width: 170,
            group: 'Transfer',
            render: (_: any, row: PlanRow) => {
                if (row._isLeg) return null;
                const flags: ReactNode[] = [];
                if ((row.capacityCappedQty ?? 0) > 0) {
                    flags.push(
                        <Tooltip key="cap" label={`Destination at capacity: ${formatCount(row.capacityCappedQty!)} units of the deficit could not be sent.`}>
                            <Badge color="orange" variant="light" size="xs">capacity −{formatCount(row.capacityCappedQty!)}</Badge>
                        </Tooltip>,
                    );
                }
                if ((row.unallocatedQty ?? 0) > 0) {
                    flags.push(
                        <Tooltip key="un" label={unallocatedReasonText(row.unallocatedReason)} multiline w={280}>
                            <Badge color="yellow" variant="light" size="xs">unplaced {formatCount(row.unallocatedQty!)}</Badge>
                        </Tooltip>,
                    );
                }
                if (row.legsEdited) {
                    flags.push(<Badge key="ed" color="blue" variant="light" size="xs">legs edited</Badge>);
                }
                return flags.length ? <Group gap={4} wrap="wrap">{flags}</Group> : null;
            },
        },
        ...(showStatusCol ? [{
            key: 'status',
            header: 'Status',
            width: 110,
            group: 'Transfer',
            render: (_: any, row: PlanRow) => row._isLeg ? null : row.status ? (
                <Badge
                    color={row.status === 'APPROVED' ? 'teal' : row.status === 'MODIFIED' ? 'blue' : row.status === 'REJECTED' ? 'red' : 'gray'}
                    variant="light"
                    size="xs"
                >
                    {row.status}
                </Badge>
            ) : null,
        } as DataSheetColumn<PlanRow>] : []),
    ];

    // ── STN columns (approved plan) ──
    const stnColumns: DataSheetColumn<RebalancePlanTransfer>[] = [
        {
            key: '_idx',
            header: '#',
            width: 44,
            render: (_, _row, idx) => <Text size="xs" c="dimmed">{idx + 1}</Text>,
        },
        ...(isNetwork ? [{
            key: '_nodeTransfer',
            header: 'Node transfer',
            width: 200,
            render: (_: any, row: RebalancePlanTransfer) => (
                <Text size="xs" c="dimmed">
                    {(row.fromNodeName ?? nameOf(row.fromWarehouseId))} → {(row.toNodeName ?? nameOf(row.toWarehouseId))}
                </Text>
            ),
        } as DataSheetColumn<RebalancePlanTransfer>] : []),
        {
            key: 'fromWarehouseId',
            header: 'From',
            width: 160,
            render: (_, row) => <Text size="sm" fw={500}>{row.fromWarehouseId}</Text>,
        },
        {
            key: 'toWarehouseId',
            header: 'To',
            width: 160,
            render: (_, row) => <Text size="sm" fw={500}>{row.toWarehouseId}</Text>,
        },
        {
            key: 'productId',
            header: 'Product',
            width: 180,
            render: (_, row) => <Text size="sm" ff="monospace">{row.productId}</Text>,
        },
        {
            key: 'stnCode',
            header: 'STN',
            width: 130,
            render: (_, row) => row.stnCode
                ? <Text size="sm" ff="monospace">{row.stnCode}</Text>
                : <Text size="xs" c="dimmed">—</Text>,
        },
        {
            key: 'qtyPlanned',
            header: 'Qty Planned',
            width: 120,
            align: 'right',
            render: (_, row) => <Text size="sm">{formatCount(Number(row.qtyPlanned))}</Text>,
        },
        {
            key: 'qtyReceived',
            header: 'Disp. / Recv.',
            width: 120,
            align: 'right',
            render: (_, row) => (
                <Text size="sm" c={row.qtyDispatched == null && row.qtyReceived == null ? 'dimmed' : undefined}>
                    {row.qtyDispatched != null ? formatCount(Number(row.qtyDispatched)) : '—'} / {row.qtyReceived != null ? formatCount(Number(row.qtyReceived)) : '—'}
                </Text>
            ),
        },
        {
            key: 'wmsGatePassCode',
            header: 'Gate pass',
            width: 200,
            render: (_, row) => {
                if (row.wmsStatus === 'FAILED') {
                    return (
                        <Tooltip label={row.wmsErrorMessage ?? 'Push failed'} multiline w={320}>
                            <Badge color="red" variant="light" size="xs">push failed</Badge>
                        </Tooltip>
                    );
                }
                if (row.wmsGatePassCode) {
                    return (
                        <Group gap={6} wrap="nowrap">
                            <Text size="sm" ff="monospace">{row.wmsGatePassCode}</Text>
                            {row.externalStatus && <Badge color="gray" variant="light" size="xs">{row.externalStatus}</Badge>}
                        </Group>
                    );
                }
                return <Text size="xs" c="dimmed">{row.stnId ? 'not pushed' : '—'}</Text>;
            },
        },
        {
            key: 'status',
            header: 'Status',
            width: 140,
            render: (_, row) => (
                <Badge
                    color={
                        row.status === 'RECEIVED' ? 'green' :
                        row.status === 'IN_TRANSIT' || row.status === 'DISPATCHED' ? 'blue' :
                        row.status === 'DELAYED' ? 'orange' :
                        row.status === 'CANCELLED' ? 'red' : 'gray'
                    }
                    variant="light"
                    size="xs"
                >
                    {row.status.replace('_', ' ')}
                </Badge>
            ),
        },
        {
            key: 'plannedDispatchDate',
            header: 'Planned Dispatch',
            width: 150,
            render: (_, row) => (
                <Text size="sm" c="dimmed">
                    {row.plannedDispatchDate ? new Date(row.plannedDispatchDate).toLocaleDateString() : '—'}
                </Text>
            ),
        },
        {
            key: 'expectedArrivalDate',
            header: 'Expected Arrival',
            width: 150,
            render: (_, row) => (
                <Text size="sm" c="dimmed">
                    {row.expectedArrivalDate ? new Date(row.expectedArrivalDate).toLocaleDateString() : '—'}
                </Text>
            ),
        },
    ];

    // ── Pivoted columns (To WH view) ──
    const pivotedToWhLabel = selectedToWh ? `To: ${warehouseNameMap.get(selectedToWh) ?? selectedToWh}` : '';

    const pivotedCheckboxCol: DataSheetColumn<PivotedRow> = {
        key: '_check',
        header: '',
        width: 40,
        sticky: true,
        sortable: false,
        filterable: false,
        renderHeader: () => {
            const allIds = pivotedRows
                .filter(r => !r.isAvailableOnly)
                .flatMap(r => Object.values(r.transfers).filter(t => !isRaised(t)).map(t => t.id));
            const allChecked = allIds.length > 0 && allIds.every(id => approvedIds.has(id));
            const someChecked = allIds.some(id => approvedIds.has(id));
            return (
                <Checkbox
                    checked={allChecked}
                    indeterminate={someChecked && !allChecked}
                    onChange={() => setApprovedIds(prev => {
                        const next = new Set(prev);
                        if (allChecked) { allIds.forEach(id => next.delete(id)); }
                        else { allIds.forEach(id => next.add(id)); }
                        return next;
                    })}
                    size="xs"
                    color="violet"
                />
            );
        },
        render: (_, row) => {
            if (row.isAvailableOnly) return null;
            const transfersHere = Object.values(row.transfers);
            if (transfersHere.length > 0 && transfersHere.every(isRaised)) {
                return <Tooltip label="Already raised as an STN"><Badge size="xs" variant="light" color="teal">STN</Badge></Tooltip>;
            }
            const ids = transfersHere.filter(t => !isRaised(t)).map(t => t.id);
            const allChecked = ids.length > 0 && ids.every(id => approvedIds.has(id));
            const someChecked = ids.some(id => approvedIds.has(id));
            return (
                <Checkbox
                    checked={allChecked}
                    indeterminate={someChecked && !allChecked}
                    onChange={() => setApprovedIds(prev => {
                        const next = new Set(prev);
                        if (allChecked) { ids.forEach(id => next.delete(id)); }
                        else { ids.forEach(id => next.add(id)); }
                        return next;
                    })}
                    size="xs"
                    color="violet"
                />
            );
        },
    };

    // Per-warehouse read-only stock columns for one physical warehouse, under a node's super group.
    const memberStockColumns = (whId: string, superGroup: string, side: 'from' | 'to'): DataSheetColumn<PivotedRow>[] => {
        const whLabel = warehouseNameMap.get(whId) ?? whId;
        const cell = (row: PivotedRow, pick: (ph: ProductHealthSnapshot) => number) => {
            const ph = productHealthMap.get(`${whId}|${row.productId}`);
            return <Text size="sm" c={ph ? undefined : 'dimmed'}>{ph ? formatCount(pick(ph)) : '—'}</Text>;
        };
        return [
            { key: `_${side}_${whId}_stock`, header: 'Stock', width: 100, align: 'right', group: whLabel, superGroup, render: (_, row) => cell(row, ph => ph.currentStockUnits) },
            { key: `_${side}_${whId}_it`, header: 'In Transit', width: 100, align: 'right', group: whLabel, superGroup, render: (_, row) => cell(row, ph => ph.inTransitUnits) },
            { key: `_${side}_${whId}_demand`, header: 'Target Demand', width: 120, align: 'right', group: whLabel, superGroup, render: (_, row) => cell(row, ph => ph.targetDemandUnits) },
        ];
    };
    const toMembers = isNetwork && selectedToWh ? (membersOfNode.get(selectedToWh) ?? []) : [];
    // Network: the To column IS a node → super group over node total + members.
    // Other scopes: the To column is a warehouse → super group is the node it belongs to.
    const toNodeOfWarehouse = selectedToWh && !isNetwork ? nodeOfWarehouse.get(selectedToWh)?.nodeName ?? null : null;
    const toSuperGroup = isNetwork && toMembers.length > 0
        ? pivotedToWhLabel
        : toNodeOfWarehouse ? `To: ${toNodeOfWarehouse}` : undefined;
    const toGroupLabel = isNetwork && toMembers.length > 0
        ? 'Node total'
        : toNodeOfWarehouse ? (warehouseNameMap.get(selectedToWh ?? '') ?? selectedToWh ?? '') : pivotedToWhLabel;

    const pivotedColumns: DataSheetColumn<PivotedRow>[] = selectedToWh ? [
        ...(canApprove ? [pivotedCheckboxCol] : []),
        {
            key: 'productId',
            header: 'Product',
            width: 220,
            sticky: true,
            render: (_, row) => {
                const name = row.productName || row.productId;
                const code = row.skuCode || row.productId;
                return (
                    <div>
                        <Text size="sm" fw={500}>{name}</Text>
                        {code !== name && <Text size="xs" c="dimmed">{code}</Text>}
                    </div>
                );
            },
        },
        {
            key: '_toStock',
            header: 'Current Stock',
            width: 120,
            align: 'right',
            group: toGroupLabel,
            superGroup: toSuperGroup,
            render: (_, row) => {
                const ph = productHealthMap.get(`${selectedToWh}|${row.productId}`);
                return <Text size="sm">{ph ? formatCount(ph.currentStockUnits) : '—'}</Text>;
            },
        },
        {
            key: '_toIT',
            header: 'In Transit',
            width: 110,
            align: 'right',
            group: toGroupLabel,
            superGroup: toSuperGroup,
            render: (_, row) => {
                const ph = productHealthMap.get(`${selectedToWh}|${row.productId}`);
                return <Text size="sm">{ph ? formatCount(ph.inTransitUnits) : '—'}</Text>;
            },
        },
        {
            key: '_toDemand',
            header: 'Target Demand',
            width: 130,
            align: 'right',
            group: toGroupLabel,
            superGroup: toSuperGroup,
            render: (_, row) => {
                const ph = productHealthMap.get(`${selectedToWh}|${row.productId}`);
                return <Text size="sm">{ph ? formatCount(ph.targetDemandUnits) : '—'}</Text>;
            },
        },
        ...(toSuperGroup && toMembers.length > 0 ? toMembers.flatMap(whId => memberStockColumns(whId, toSuperGroup, 'to')) : []),
        ...fromWhIds.flatMap((fromWhId): DataSheetColumn<PivotedRow>[] => {
            const whName = warehouseNameMap.get(fromWhId) ?? fromWhId;
            // In a network plan a From column is a NODE: a super group spans its node totals and
            // then one group per member warehouse (stock, in transit, demand, and the editable leg
            // quantity that actually leaves it), since every STN is warehouse to warehouse.
            const memberIds = isNetwork ? (membersOfNode.get(fromWhId) ?? []) : [];
            const isNodeColumn = memberIds.length > 0;
            // A warehouse column in a non-network plan is filed under its geo node, so the
            // header reads "From: Delhi" over "DEL-1 | DEL-2" even when each is its own transfer.
            const fromNode = !isNodeColumn ? nodeOfWarehouse.get(fromWhId)?.nodeName ?? null : null;
            const superGroup = isNodeColumn ? `From: ${whName}` : fromNode ? `From: ${fromNode}` : (nodeOfWarehouse.size > 0 ? 'From: not in a node' : undefined);
            const groupLabel = isNodeColumn ? 'Node total' : (superGroup ? whName : `From: ${whName}`);
            const memberColumns: DataSheetColumn<PivotedRow>[] = memberIds.flatMap(memberId => [
                ...memberStockColumns(memberId, superGroup!, 'from'),
                {
                    key: `_${fromWhId}_${memberId}_qty`,
                    header: 'Transfer Qty',
                    width: 130,
                    align: 'right',
                    group: warehouseNameMap.get(memberId) ?? memberId,
                    superGroup,
                    render: (_, row) => {
                        const transfer = row.transfers[fromWhId];
                        if (!transfer) return <Text size="xs" c="dimmed">—</Text>;
                        const committed = legQtyFrom(transfer, memberId);
                        const dests = (transfer.legs ?? []).filter(l => l.fromWarehouseId === memberId).map(l => warehouseNameMap.get(l.toWarehouseId) ?? l.toWarehouseId);
                        if (!canApprove || isRaised(transfer)) {
                            return committed > 0
                                ? <div><Text size="sm">{formatCount(committed)}</Text><Text size="xs" c="dimmed" style={{ fontSize: 10, lineHeight: 1.2 }}>→ {dests.join(', ')}</Text></div>
                                : <Text size="xs" c="dimmed">—</Text>;
                        }
                        const inputKey = `leg_${transfer.id}|${memberId}`;
                        const draft = inputDraftValues.get(inputKey);
                        const displayValue = draft !== undefined ? draft : (committed > 0 ? String(committed) : '');
                        const isEdited = localLegEdits.has(`${transfer.id}|${memberId}`);
                        return (
                            <div style={{ textAlign: 'right' }}>
                                <input
                                    type="number"
                                    value={displayValue}
                                    min={0}
                                    placeholder="—"
                                    onChange={e => {
                                        const raw = e.target.value;
                                        setInputDraftValues(prev => new Map(prev).set(inputKey, raw));
                                        const num = Math.ceil(Number(raw));
                                        if (raw !== '' && !isNaN(num) && num >= 0) {
                                            setLocalLegEdits(prev => new Map(prev).set(`${transfer.id}|${memberId}`, num));
                                        } else if (raw === '') {
                                            setLocalLegEdits(prev => new Map(prev).set(`${transfer.id}|${memberId}`, 0));
                                        }
                                    }}
                                    onFocus={() => setFocusedInputId(inputKey)}
                                    onBlur={() => {
                                        setFocusedInputId(null);
                                        setInputDraftValues(prev => { const next = new Map(prev); next.delete(inputKey); return next; });
                                    }}
                                    style={{
                                        width: 80,
                                        textAlign: 'right',
                                        border: 'none',
                                        borderBottom: `2px solid ${focusedInputId === inputKey ? 'var(--mantine-color-indigo-6)' : isEdited ? 'var(--mantine-color-indigo-3)' : 'var(--mantine-color-gray-4)'}`,
                                        background: 'transparent',
                                        outline: 'none',
                                        fontSize: 'var(--mantine-font-size-sm)',
                                        padding: '2px 4px',
                                        fontFamily: 'inherit',
                                    }}
                                />
                                <Text size="xs" c="dimmed" style={{ fontSize: 10, lineHeight: 1.2 }}>
                                    {dests.length > 0 ? `→ ${dests.join(', ')}` : 'no leg yet'}
                                </Text>
                            </div>
                        );
                    },
                },
            ]);
            return [
                {
                    key: `_${fromWhId}_stock`,
                    header: 'Current Stock',
                    width: 120,
                    align: 'right',
                    group: groupLabel,
                    superGroup,
                    render: (_, row) => {
                        const ph = productHealthMap.get(`${fromWhId}|${row.productId}`);
                        return <Text size="sm">{ph ? formatCount(ph.currentStockUnits) : '—'}</Text>;
                    },
                },
                {
                    key: `_${fromWhId}_it`,
                    header: 'In Transit',
                    width: 110,
                    align: 'right',
                    group: groupLabel,
                    superGroup,
                    render: (_, row) => {
                        const ph = productHealthMap.get(`${fromWhId}|${row.productId}`);
                        return <Text size="sm">{ph ? formatCount(ph.inTransitUnits) : '—'}</Text>;
                    },
                },
                {
                    key: `_${fromWhId}_demand`,
                    header: 'Target Demand',
                    width: 130,
                    align: 'right',
                    group: groupLabel,
                    superGroup,
                    render: (_, row) => {
                        const ph = productHealthMap.get(`${fromWhId}|${row.productId}`);
                        return <Text size="sm">{ph ? formatCount(ph.targetDemandUnits) : '—'}</Text>;
                    },
                },
                {
                    key: `_${fromWhId}_qty`,
                    header: isNodeColumn ? 'Node Qty' : 'Transfer Qty',
                    width: 130,
                    align: 'right',
                    group: groupLabel,
                    superGroup,
                    render: (_, row) => {
                        const transfer = row.transfers[fromWhId];
                        // On a node column the quantity is the sum of the member legs — edit those.
                        if (isNodeColumn) {
                            if (!transfer) return <Text size="sm" c="dimmed">—</Text>;
                            const sum = memberIds.reduce((a, m) => a + legQtyFrom(transfer, m), 0);
                            const unplaced = (transfer.unallocatedQty ?? 0);
                            return (
                                <div style={{ textAlign: 'right' }}>
                                    <Text size="sm" fw={500}>{formatCount(sum)}</Text>
                                    {unplaced > 0 && <Text size="xs" c="orange.8" style={{ fontSize: 10, lineHeight: 1.2 }}>+{formatCount(unplaced)} unplaced</Text>}
                                </div>
                            );
                        }
                        if (!transfer) {
                            if (!canApprove) return <Text size="sm" c="dimmed">—</Text>;
                            const draftKey = `new_${fromWhId}|${selectedToWh}|${row.productId}`;
                            const transferKey = `${fromWhId}|${selectedToWh}|${row.productId}`;
                            const committed = newTransferDrafts.get(transferKey);
                            const rawDraft = inputDraftValues.get(draftKey);
                            const displayValue = rawDraft !== undefined ? rawDraft : (committed !== undefined ? String(committed) : '');
                            return (
                                <div style={{ textAlign: 'right' }}>
                                    <input
                                        type="number"
                                        value={displayValue}
                                        min={0}
                                        placeholder="—"
                                        onChange={e => {
                                            const raw = e.target.value;
                                            setInputDraftValues(prev => new Map(prev).set(draftKey, raw));
                                            const num = Math.ceil(Number(raw));
                                            if (raw !== '' && !isNaN(num) && num >= 0) {
                                                setNewTransferDrafts(prev => new Map(prev).set(transferKey, num));
                                            } else if (raw === '') {
                                                setNewTransferDrafts(prev => {
                                                    const next = new Map(prev);
                                                    next.delete(transferKey);
                                                    return next;
                                                });
                                            }
                                        }}
                                        onFocus={() => setFocusedInputId(draftKey)}
                                        onBlur={() => {
                                            setFocusedInputId(null);
                                            setInputDraftValues(prev => {
                                                const next = new Map(prev);
                                                next.delete(draftKey);
                                                return next;
                                            });
                                        }}
                                        style={{
                                            width: 80,
                                            textAlign: 'right',
                                            border: 'none',
                                            borderBottom: `2px solid ${focusedInputId === draftKey ? 'var(--mantine-color-indigo-6)' : 'var(--mantine-color-gray-4)'}`,
                                            background: 'transparent',
                                            outline: 'none',
                                            fontSize: 'var(--mantine-font-size-sm)',
                                            padding: '2px 4px',
                                            fontFamily: 'inherit',
                                        }}
                                    />
                                </div>
                            );
                        }
                        if (!canApprove || isRaised(transfer)) {
                            return <Text size="sm">{formatCount((transfer.qtyApproved ?? transfer.qtyRecommended))}</Text>;
                        }
                        const inputKey = `pivot_${transfer.id}`;
                        const committedVal = localQtyOverrides.get(transfer.id) ?? transfer.qtyApproved ?? transfer.qtyRecommended;
                        const isOverridden = localQtyOverrides.has(transfer.id);
                        const pivotDraft = inputDraftValues.get(inputKey);
                        const displayValue = pivotDraft !== undefined ? pivotDraft : String(committedVal ?? '');
                        return (
                            <div style={{ textAlign: 'right' }}>
                                <input
                                    type="number"
                                    value={displayValue}
                                    min={0}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        setInputDraftValues(prev => new Map(prev).set(inputKey, raw));
                                        const num = Math.ceil(Number(raw));
                                        if (raw !== '' && !isNaN(num) && num >= 0) {
                                            setLocalQtyOverrides(prev => new Map(prev).set(transfer.id, num));
                                        }
                                    }}
                                    onFocus={() => setFocusedInputId(inputKey)}
                                    onBlur={() => {
                                        setFocusedInputId(null);
                                        setInputDraftValues(prev => {
                                            const next = new Map(prev);
                                            next.delete(inputKey);
                                            return next;
                                        });
                                    }}
                                    style={{
                                        width: 80,
                                        textAlign: 'right',
                                        border: 'none',
                                        borderBottom: `2px solid ${focusedInputId === inputKey ? 'var(--mantine-color-indigo-6)' : 'var(--mantine-color-gray-4)'}`,
                                        background: 'transparent',
                                        outline: 'none',
                                        fontSize: 'var(--mantine-font-size-sm)',
                                        padding: '2px 4px',
                                        fontFamily: 'inherit',
                                    }}
                                />
                                {isOverridden && (
                                    <Text size="xs" c="dimmed" style={{ fontSize: 10, lineHeight: 1.2 }}>
                                        rec: {formatCount(transfer.qtyRecommended)}
                                    </Text>
                                )}
                            </div>
                        );
                    },
                },
                ...memberColumns,
            ];
        }),
    ] : [];

    const transferCountLabel = hasTransfers
        ? `${filteredTransfers.length}${allTransfers.length !== filteredTransfers.length ? ` of ${allTransfers.length}` : ''} transfer${filteredTransfers.length !== 1 ? 's' : ''}`
        : undefined;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Header */}
            <Group justify="space-between" pb="md" style={{ flexShrink: 0 }}>
                <Group gap="sm">
                    <Button
                        variant="subtle"
                        color="violet"
                        size="xs"
                        leftSection={<IconChevronLeft size={14} />}
                        onClick={handleBack}
                    >
                        Plans
                    </Button>

                    {/* Inline name edit */}
                    {editingName ? (
                        <TextInput
                            value={nameValue}
                            onChange={e => setNameValue(e.currentTarget.value)}
                            onBlur={handleNameSave}
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleNameSave();
                                if (e.key === 'Escape') setEditingName(false);
                            }}
                            size="xs"
                            fw={600}
                            autoFocus
                            w={220}
                        />
                    ) : (
                        <Group gap={4} align="center">
                            <Text fw={600} size="md">{plan?.name ?? nameValue}</Text>
                            {canApprove && (
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    size="xs"
                                    onClick={() => { setNameValue(plan?.name ?? ''); setEditingName(true); }}
                                    title="Rename plan"
                                >
                                    <IconPencil size={12} />
                                </ActionIcon>
                            )}
                        </Group>
                    )}

                    {plan && (
                        <Badge color={statusColor(plan.status)} variant="light" size="sm">
                            {plan.status}
                        </Badge>
                    )}
                    {plan && (
                        <Badge color={isNetwork ? 'indigo' : planScope.level === 'NODE' ? 'teal' : 'gray'} variant="outline" size="sm">
                            {scopeLabel(planScope)}
                        </Badge>
                    )}
                </Group>

                <Group gap="xs">
                    {saveError && <Text size="xs" c="red">{saveError}</Text>}
                    {computeError && <Text size="xs" c="red">{computeError}</Text>}

                    {/* Re-compute — DRAFT plans only */}
                    {canApprove && !isNew && (
                        <ActionIcon
                            variant="subtle"
                            color="violet"
                            size="md"
                            loading={computing}
                            onClick={handleRecompute}
                            title="Re-compute plan with latest rebalance settings"
                        >
                            <IconRefresh size={16} />
                        </ActionIcon>
                    )}

                    {/* Push to WMS — approved plans with legs not yet in the WMS */}
                    {showStns && wmsPushAllowed && pendingWmsCount > 0 && (
                        <Button
                            size="sm"
                            variant="light"
                            color="indigo"
                            loading={pushingWms}
                            onClick={handlePushToWms}
                        >
                            Push {pendingWmsCount} to WMS
                        </Button>
                    )}

                    {/* Delete */}
                    {!isNew && (
                        <ActionIcon
                            variant="subtle"
                            color="red"
                            size="md"
                            loading={deleting}
                            onClick={handleDelete}
                            title="Delete plan"
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    )}

                    {/* Save qty edits — DRAFT only, when there are unsaved edits */}
                    {canApprove && (localQtyOverrides.size > 0 || newTransferDrafts.size > 0 || localLegEdits.size > 0) && (
                        <Button
                            size="sm"
                            variant="light"
                            color="violet"
                            loading={savingEdits}
                            onClick={handleSave}
                        >
                            Save ({localQtyOverrides.size + newTransferDrafts.size + localLegEdits.size})
                        </Button>
                    )}

                    {/* Override in Bulk — DRAFT only */}
                    {canApprove && hasTransfers && (
                        <>
                            <input
                                ref={uploadRef}
                                type="file"
                                accept=".csv"
                                style={{ display: 'none' }}
                                onChange={handleCsvUpload}
                            />
                            <Button
                                size="sm"
                                variant="subtle"
                                color="violet"
                                onClick={() => setBulkOverrideOpen(true)}
                            >
                                Override in Bulk
                            </Button>
                        </>
                    )}

                    {/* Approve — always visible on a draft; disabled until at least one row is ticked */}
                    {canApprove && hasTransfers && (
                        <Tooltip label={scopedApprovedIds.size === 0 ? 'Tick at least one transfer on screen to approve' : `${selectedToWh ? `Only the ${scopedApprovedIds.size} ticked transfer${scopedApprovedIds.size !== 1 ? 's' : ''} into ${nameOf(selectedToWh)}` : `${scopedApprovedIds.size} ticked transfer${scopedApprovedIds.size !== 1 ? 's' : ''}`} — one STN per warehouse pair`}>
                            <Button
                                size="sm"
                                radius="md"
                                color="violet"
                                leftSection={<IconCheck size={16} />}
                                onClick={handleApprove}
                                loading={saving}
                                disabled={scopedApprovedIds.size === 0}
                            >
                                Approve{selectedToWh ? ` → ${nameOf(selectedToWh)}` : ''} ({scopedApprovedIds.size}){raisedCount > 0 ? ` · ${raisedCount} raised` : ''}
                            </Button>
                        </Tooltip>
                    )}
                </Group>
            </Group>

            {/* Staleness banner */}
            {plan && isPlanStale(plan) && (
                <Group
                    gap="sm"
                    px="md"
                    py="xs"
                    mb="sm"
                    style={{
                        background: 'var(--mantine-color-orange-0)',
                        border: '1px solid var(--mantine-color-orange-3)',
                        borderRadius: 8,
                        flexShrink: 0,
                    }}
                >
                    <IconAlertTriangle size={15} color="var(--mantine-color-orange-7)" />
                    <Text size="sm" c="orange.8" style={{ flex: 1 }}>
                        Last calculated {new Date(plan.planDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · stock levels and demand may have changed
                    </Text>
                    <Button
                        variant="light"
                        color="orange"
                        size="xs"
                        radius="md"
                        leftSection={<IconRefresh size={12} />}
                        loading={computing}
                        onClick={handleRecompute}
                    >
                        Recalculate
                    </Button>
                </Group>
            )}

            {/* DataSheet area — full width, no settings panel */}
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    overflow: 'hidden',
                    border: '1px solid var(--mantine-color-gray-2)',
                    borderRadius: 8,
                    minHeight: 0,
                }}
            >
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    {planLoading ? (
                        <Stack align="center" justify="center" style={{ flex: 1 }} gap="sm">
                            <Loader size="sm" color="violet" />
                        </Stack>
                    ) : computing ? (
                        <Stack align="center" justify="center" style={{ flex: 1 }} gap="sm">
                            <Loader size="sm" color="violet" />
                            <Text size="sm" c="dimmed">Computing optimal transfers…</Text>
                        </Stack>
                    ) : isNew ? (
                        <Stack align="center" justify="center" style={{ flex: 1 }} gap="md">
                            <Text c="dimmed" size="sm" ta="center" maw={320}>
                                Click "Compute Plan" to generate recommended transfers using your configured rebalance settings.
                            </Text>
                            {nodeList.length > 0 && (
                                <Select
                                    label="Scope"
                                    description="What the optimizer treats as one stock position"
                                    size="xs"
                                    w={320}
                                    value={scope.level === 'NODE' ? `NODE:${scope.nodeId}` : scope.level}
                                    onChange={v => {
                                        if (!v) return;
                                        if (v.startsWith('NODE:')) {
                                            const id = v.slice(5);
                                            setScope({ level: 'NODE', nodeId: id, nodeName: nodeList.find(n => n.id === id)?.name ?? null });
                                        } else {
                                            setScope({ level: v as RebalanceScope['level'] });
                                        }
                                    }}
                                    data={[
                                        { value: 'NETWORK', label: 'Across network — nodes as stock positions' },
                                        ...nodeList.map(n => ({ value: `NODE:${n.id}`, label: `Within ${n.name} — its warehouses against each other` })),
                                        { value: 'WAREHOUSE', label: 'Every warehouse, ignoring nodes' },
                                    ]}
                                />
                            )}
                            <Group gap="xs">
                                <Button
                                    color="violet"
                                    size="sm"
                                    radius="md"
                                    loading={computing}
                                    rightSection={<IconArrowRight size={16} />}
                                    onClick={handleCompute}
                                >
                                    Compute Plan
                                </Button>
                                <ScheduleButton
                                    matchOn="scopeKey"
                                    size="sm"
                                    radius="md"
                                    title={`Schedule: rebalance ${scopeName}`}
                                    description="The optimizer runs on this schedule with the saved rebalance settings. The result shows as a card on Home; opening it brings you here with the plan computed."
                                    autoSave={{ label: 'Save the plan on every run', description: `Each run keeps its result as one draft plan, "Rebalance ${scopeName} · scheduled", recomputed in place. Transfers you edited, added or rejected on that draft stay as they are and the rest is planned around them. Nothing is approved and no transfer is created.` }}
                                    alert={{
                                        alertType: 'REBALANCE_PLAN_DUE',
                                        alertLabel: `Rebalance ${scopeName}`,
                                        // The link back here is the type's default (action.constants): ?scope=<scopeKey>.
                                        context: {
                                            scope: { level: scope.level, ...(scope.nodeId ? { nodeId: scope.nodeId } : {}) },
                                            scopeKey,
                                            scopeName,
                                        },
                                    }}
                                />
                            </Group>
                            {computeError && <Text size="xs" c="red">{computeError}</Text>}
                        </Stack>
                    ) : !hasTransfers ? (
                        <Stack align="center" justify="center" style={{ flex: 1 }}>
                            <Text c="dimmed" size="sm">No transfers in this plan.</Text>
                        </Stack>
                    ) : (
                        <>
                            {/* To WH filter — switch between flat and pivoted view */}
                            <Group
                                gap="sm"
                                px="sm"
                                pt="sm"
                                pb="xs"
                                justify="space-between"
                                style={{ flexShrink: 0, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                            >
                                <Group gap="sm">
                                    <Text size="xs" c="dimmed" fw={500}>To Warehouse:</Text>
                                    <Select
                                        placeholder="All (flat view)"
                                        data={toWhOptions}
                                        value={selectedToWh}
                                        onChange={v => { setSelectedToWh(v); setRouteFilter({}); setSkuVisibility('all'); }}
                                        clearable
                                        size="xs"
                                        w={200}
                                    />
                                </Group>
                                {selectedToWh && (
                                    <SegmentedControl
                                        size="xs"
                                        value={skuVisibility}
                                        onChange={v => setSkuVisibility(v as SkuVisibility)}
                                        data={[
                                            { label: 'All SKUs', value: 'all' },
                                            { label: 'Recommended', value: 'recommended' },
                                            { label: 'Available only', value: 'available' },
                                        ]}
                                        color="violet"
                                    />
                                )}
                            </Group>

                            {selectedToWh ? (
                                <DataSheet
                                    data={pivotedRows}
                                    columns={pivotedColumns}
                                    getRowKey={row => row.productId}
                                    getRowClassName={row => row.isAvailableOnly ? styles.stateAvailable : styles.stateRecommended}
                                    subtitle={`${pivotedRows.filter(r => !r.isAvailableOnly).length} recommended · ${pivotedRows.filter(r => r.isAvailableOnly).length} available`}
                                    filters={pivotDataSheetFilters}
                                    enableSearch
                                    searchPlaceholder="Search by SKU or product name…"
                                    searchValue={searchQuery}
                                    onSearchChange={setSearchQuery}
                                    aggregateRow={pivotAggregateRow}
                                    maxHeight={isApproved ? 'calc(50vh - 160px)' : 'calc(100vh - 320px)'}
                                    enableDownload={hasTransfers}
                                    onDownload={() => downloadCsv(plan!, filteredTransfers, canApprove ? localQtyOverrides : undefined)}
                                    emptyState={
                                        <Text c="dimmed" size="sm" ta="center" p="xl">
                                            No transfers for this warehouse.
                                        </Text>
                                    }
                                />
                            ) : (
                                <DataSheet
                                    data={filteredTransfers as PlanRow[]}
                                    columns={columns}
                                    getRowKey={row => row.id}
                                    getSubRows={isNetwork ? legRowsOf : undefined}
                                    subtitle={transferCountLabel}
                                    filters={hasTransfers ? dataSheetFilters : undefined}
                                    aggregateRow={aggregateRow}
                                    enableSearch
                                    searchPlaceholder="Search by SKU or product name…"
                                    searchValue={searchQuery}
                                    onSearchChange={setSearchQuery}
                                    maxHeight={isApproved ? 'calc(50vh - 160px)' : 'calc(100vh - 320px)'}
                                    enableDownload={hasTransfers}
                                    onDownload={() => downloadCsv(plan!, filteredTransfers, canApprove ? localQtyOverrides : undefined)}
                                    emptyState={
                                        <Text c="dimmed" size="sm" ta="center" p="xl">
                                            No transfers match the current filters.
                                        </Text>
                                    }
                                />
                            )}

                            {/* STNs table — after any approval, partial or full */}
                            {showStns && (
                                <div style={{ borderTop: '1px solid var(--mantine-color-gray-2)', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                    {stnsLoading ? (
                                        <Stack align="center" justify="center" p="md" gap="xs">
                                            <Loader size="xs" color="violet" />
                                        </Stack>
                                    ) : (
                                        <DataSheet
                                            data={stns}
                                            columns={stnColumns}
                                            getRowKey={row => row.id}
                                            title="Stock Transfer Notes"
                                            subtitle={`${stns.length} STN${stns.length !== 1 ? 's' : ''}`}
                                            maxHeight="calc(50vh - 160px)"
                                            emptyState={<Text c="dimmed" size="sm" p="md">No stock transfer notes for this plan.</Text>}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {plan && reviewOpen && (
                <ApproveReviewModal
                    preview={reviewPreview}
                    loading={reviewLoading}
                    approving={saving}
                    pushToWms={pushToWms}
                    onPushToWmsChange={setPushToWms}
                    onCancel={() => setReviewOpen(false)}
                    onApprove={doApprove}
                />
            )}

            {plan && legEditorFor && (
                <LegEditorModal
                    transfer={legEditorFor}
                    plan={plan}
                    nodes={nodeList}
                    lanes={lanes}
                    nameOf={nameOf}
                    onClose={() => setLegEditorFor(null)}
                    onSaved={updated => { setLoadedPlan(updated); onPlanUpdated(updated); }}
                />
            )}

            {/* Bulk qty override modal */}
            <Modal
                opened={bulkOverrideOpen}
                onClose={() => { setBulkOverrideOpen(false); setBulkOverridePhase('idle'); }}
                title={<Text fw={600} size="sm">Bulk Override Transfer Quantities</Text>}
                size="md"
                radius="md"
                centered
            >
                {bulkOverridePhase === 'idle' ? (
                    <Stack gap="md">
                        <Text size="sm" c="dimmed">
                            Upload a CSV to update approved quantities for multiple transfers at once.
                            Only the <strong>Approved Qty</strong> column is applied — all other values are used only for matching.
                        </Text>

                        <div>
                            <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={8}>Required columns</Text>
                            <Stack gap={4}>
                                {([
                                    ['Transfer ID', 'Primary match (use template)'],
                                    ['SKU', 'Fallback match'],
                                    ['From Warehouse', 'Fallback match'],
                                    ['To Warehouse', 'Fallback match'],
                                    ['Approved Qty', 'New quantity — must be a positive integer'],
                                ] as const).map(([col, note]) => (
                                    <Group key={col} gap="xs" align="flex-start">
                                        <Text size="xs" ff="monospace" c="violet" w={130} fw={500}>{col}</Text>
                                        <Text size="xs" c="dimmed">{note}</Text>
                                    </Group>
                                ))}
                            </Stack>
                        </div>

                        <Button
                            variant="light"
                            color="violet"
                            size="sm"
                            leftSection={<IconDownload size={14} />}
                            onClick={() => downloadCsv(plan!, allTransfers, localQtyOverrides)}
                        >
                            Download current plan as template
                        </Button>

                        <Button
                            variant="outline"
                            color="gray"
                            size="sm"
                            leftSection={<IconUpload size={14} />}
                            onClick={() => uploadRef.current?.click()}
                            fullWidth
                        >
                            Choose CSV file to upload
                        </Button>
                    </Stack>
                ) : (
                    <Stack align="center" gap="sm" py="md">
                        <IconCheck size={36} color="var(--mantine-color-green-6)" />
                        <Text size="sm" fw={600}>
                            {bulkOverrideCount} transfer{bulkOverrideCount !== 1 ? 's' : ''} updated
                        </Text>
                        <Text size="xs" c="dimmed">Quantities are saved locally. Use "Save" to persist, or "Save &amp; Approve" to finalise.</Text>
                        <Group gap="xs" mt="xs">
                            <Button
                                variant="subtle"
                                color="gray"
                                size="sm"
                                onClick={() => setBulkOverridePhase('idle')}
                            >
                                Upload another file
                            </Button>
                            <Button
                                color="violet"
                                size="sm"
                                onClick={() => { setBulkOverrideOpen(false); setBulkOverridePhase('idle'); }}
                            >
                                Done
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Modal>
        </div>
    );
};

// ─── Root ──────────────────────────────────────────────────────────────────────

interface RebalancePlansTabProps {
    initialPlan?: RebalancePlan | null;
    /** With `initialScope`: compute the new plan on arrival (Home plan card deep link). */
    autoCompute?: boolean;
    /** When set, opens straight into a new plan with this scope preselected. */
    initialScope?: RebalanceScope | null;
}

const RebalancePlansTab: FC<RebalancePlansTabProps> = ({ initialPlan, initialScope, autoCompute = false }) => {
    const [view, setView] = useState<View>(initialPlan || initialScope ? 'plan' : 'list');
    const [selectedPlan, setSelectedPlan] = useState<RebalancePlan | null>(initialPlan ?? null);

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {view === 'plan' ? (
                <PlanView
                    key={selectedPlan?.id ?? 'new'}
                    plan={selectedPlan}
                    initialScope={initialScope}
                    autoCompute={autoCompute}
                    onBack={() => { setSelectedPlan(null); setView('list'); }}
                    onPlanUpdated={updated => setSelectedPlan(updated)}
                    onDeleted={() => { setSelectedPlan(null); setView('list'); }}
                />
            ) : (
                <PlanList
                    onNew={() => { setSelectedPlan(null); setView('plan'); }}
                    onView={plan => { setSelectedPlan(plan); setView('plan'); }}
                />
            )}
        </div>
    );
};

export default RebalancePlansTab;
